import { useState, useMemo, useEffect, Fragment } from "react";
import { supabase } from "./supabase";
import { downloadEntrataCsv } from "./entrata";

// ─── SEED DATA (preserved) ───────────────────────────────────────────────────
const CONNECTORS = [
  { id:"entrata",  name:"Entrata",         logo:"E",   logoColor:"#0056D6", category:"Property Management", authType:"OAuth2 + API Key",    status:"ga",      jeEp:"POST /property/ledger/createJournalEntries",     glEp:"GET /property/ledger/getGeneralLedger" },
  { id:"yardi",    name:"Yardi Voyager",   logo:"Y",   logoColor:"#E8502A", category:"Property Management", authType:"SOAP / Basic Auth",   status:"ga",      jeEp:"SOAP ItfImportJournalEntry",                     glEp:"SOAP GetGLTransactions" },
  { id:"mri",      name:"MRI Software",   logo:"M",   logoColor:"#003087", category:"Property Management", authType:"OAuth2 / Bearer",     status:"ga",      jeEp:"POST /gl/v1/journal-entries",                    glEp:"GET /gl/v1/transactions" },
  { id:"realpage", name:"RealPage",        logo:"R",   logoColor:"#00B2A9", category:"Property Management", authType:"OAuth2",              status:"beta",    jeEp:"POST /accounting/v2/journal-entries/batch",      glEp:"GET /accounting/v2/gl-transactions" },
  { id:"netsuite", name:"NetSuite",        logo:"N",   logoColor:"#009FDA", category:"Mid-Market ERP",      authType:"OAuth1 / TBA",        status:"ga",      jeEp:"POST /record/v1/journalentry",                   glEp:"GET /record/v1/transaction" },
  { id:"intacct",  name:"Sage Intacct",    logo:"SI",  logoColor:"#00A651", category:"Mid-Market ERP",      authType:"XML Session Auth",    status:"ga",      jeEp:"GLBATCH create (XML API)",                       glEp:"GLDETAIL readByQuery" },
  { id:"qbe",      name:"QuickBooks Ent.", logo:"QB",  logoColor:"#2CA01C", category:"SMB ERP",             authType:"OAuth2 / IIF",        status:"beta",    jeEp:"POST /v3/company/{realmId}/journalentry",        glEp:"GET /v3/company/{realmId}/query" },
  { id:"sap",      name:"SAP S/4HANA",    logo:"SAP", logoColor:"#0070F2", category:"Enterprise ERP",      authType:"OAuth2 / BTP",        status:"roadmap", jeEp:"POST /API_JOURNALENTRYITEMBASIC_SRV",             glEp:"GET /API_ODATA_GLACCOUNTLINEITEM_SRV" },
  { id:"oracle",   name:"Oracle Fusion",  logo:"OF",  logoColor:"#C74634", category:"Enterprise ERP",      authType:"OAuth2 / JWT",        status:"roadmap", jeEp:"POST /fscmRestApi/resources/.../journalEntries", glEp:"GET /fscmRestApi/resources/.../generalLedger" },
];

const GROUP_ATTRS = ["region","division","ownership"];

// ─── HELPERS (preserved) ─────────────────────────────────────────────────────
const fmt = n => new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"}).format(n||0);
const fmtShort = n => {
  if (Math.abs(n) >= 1000) return `$${(n/1000).toFixed(1)}k`;
  return fmt(n);
};

function computeLines(amount, method, props, manualPcts) {
  const total = parseFloat(amount)||0;
  if (!props.length||!total) return [];
  let w={};
  if (method.driver==="equal")     props.forEach(p=>w[p.id]=1);
  else if (method.driver==="units") props.forEach(p=>w[p.id]=p.units);
  else if (method.driver==="employees") props.forEach(p=>w[p.id]=p.employees);
  else if (method.driver==="manual") props.forEach(p=>w[p.id]=parseFloat(manualPcts[p.id]||0));
  const tw=Object.values(w).reduce((a,b)=>a+b,0);
  if (!tw) return [];
  let alloc=0;
  return props.map((p,i)=>{
    const isLast=i===props.length-1;
    const amt=isLast?parseFloat((total-alloc).toFixed(2)):parseFloat(((w[p.id]/tw)*total).toFixed(2));
    alloc+=amt;
    return {property:p, amount:amt, pct:(w[p.id]/tw)*100};
  });
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,500;9..144,700;9..144,900&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#F0EEE6;--s1:#FAF9F5;--s2:#F2EFE7;--s3:#E9E5D9;
  --b1:#E3DED0;--b2:#D2CBB8;--b3:#BAB19A;
  --tx:#403D35;--txb:#1A1915;--mu:#6F6A5C;--di:#B7B09E;
  --acc:#C15F3C;--acc2:#A94E30;--accBg:rgba(193,95,60,.07);--accBd:rgba(193,95,60,.35);
  --gold:#8C6D2F;--goldBg:rgba(140,109,47,.08);--goldBd:rgba(140,109,47,.35);
  --green:#2E7D4F;--greenBg:rgba(46,125,79,.08);--greenBd:rgba(46,125,79,.35);
  --red:#B3382C;--redBg:rgba(179,56,44,.06);--redBd:rgba(179,56,44,.35);
  --blue:#3B6FA0;
  --sans:'IBM Plex Sans',-apple-system,'Segoe UI',sans-serif;
  --mono:'IBM Plex Mono',monospace;
  --head:'Fraunces',serif;
  --r:6px;
}
html,body,#root{height:100%;overflow:hidden}
body{background:var(--bg);color:var(--tx);font-family:var(--sans);font-size:13px;line-height:1.5;-webkit-font-smoothing:antialiased}
input,select,button,textarea{font-family:inherit}

/* Layout Shell */
.shell{display:grid;grid-template-rows:52px 1fr;grid-template-columns:300px 1fr;height:100vh;overflow:hidden}
.hdr{grid-column:1/-1;display:flex;align-items:center;gap:14px;padding:0 20px;border-bottom:1px solid var(--b1);background:var(--s1);z-index:100}
.queue-panel{grid-row:2;overflow:hidden;display:flex;flex-direction:column;border-right:1px solid var(--b1);background:var(--s1)}
.main-pane{grid-row:2;overflow-y:auto;overflow-x:hidden;background:var(--bg)}

/* Header */
.logo{font-family:var(--head);font-size:19px;font-weight:900;letter-spacing:-.3px;white-space:nowrap;color:var(--txb);cursor:pointer}
.logo em{color:var(--acc);font-style:normal}
.mod-switch{display:flex;align-items:center;border:1px solid var(--b2);border-radius:7px;overflow:hidden;background:var(--s2)}
.mod{padding:4px 12px;font-size:10.5px;font-weight:600;letter-spacing:.03em;color:var(--mu);text-decoration:none;cursor:pointer;transition:all .12s;line-height:1.4}
.mod.on{background:var(--acc);color:#fff}
.mod:not(.on):hover{background:var(--s3);color:var(--tx)}
.hdr-pills{margin-left:auto;display:flex;align-items:center;gap:8px}
.hpill{font-size:9px;padding:3px 9px;border:1px solid;border-radius:4px;letter-spacing:.1em;text-transform:uppercase;cursor:default;font-weight:600}
.hpill-acc{border-color:var(--accBd);color:var(--acc)}
.hpill-green{border-color:var(--greenBd);color:var(--green)}
.hpill-gold{border-color:var(--goldBd);color:var(--gold)}
.hdr-gear{background:none;border:1px solid var(--b2);border-radius:6px;color:var(--mu);width:30px;height:30px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;transition:all .15s}
.hdr-gear:hover{border-color:var(--acc);color:var(--acc)}

/* Queue Panel */
.qp-head{padding:12px 16px;border-bottom:1px solid var(--b1);display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
.qp-title{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--mu);font-weight:600}
.qp-count{font-size:10px;padding:1px 7px;border:1px solid var(--accBd);border-radius:4px;color:var(--acc);margin-left:8px;font-weight:600}
.qp-list{flex:1;overflow-y:auto;padding:4px 0}
.qi{padding:10px 16px;cursor:pointer;border-left:2px solid transparent;transition:all .1s;border-bottom:1px solid var(--b1)}
.qi:hover{background:var(--s2)}
.qi.active{background:var(--s2);border-left-color:var(--acc)}
.qi.allocated{opacity:.5}
.qi-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:3px}
.qi-vendor{font-size:12.5px;font-weight:600;color:var(--txb);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px}
.qi-amt{font-size:11.5px;font-weight:600;color:var(--txb);font-family:var(--mono);font-variant-numeric:tabular-nums}
.qi-meta{font-size:10.5px;color:var(--mu);display:flex;gap:8px;align-items:center}
.qi-badge{font-size:8px;padding:1px 5px;border:1px solid;border-radius:3px;letter-spacing:.07em;text-transform:uppercase}

.qp-section{padding:8px 16px 4px;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:var(--di);border-top:1px solid var(--b1);margin-top:4px;flex-shrink:0}

.qp-nav{border-top:1px solid var(--b1);flex-shrink:0;padding:6px 0}
.qn-btn{display:flex;align-items:center;gap:8px;width:100%;background:none;border:none;color:var(--mu);font-family:var(--sans);font-size:11.5px;font-weight:500;padding:8px 16px;cursor:pointer;transition:all .1s;text-align:left;border-left:2px solid transparent}
.qn-btn:hover{color:var(--tx);background:var(--s2)}
.qn-btn.on{color:var(--acc);background:var(--s2);border-left-color:var(--acc);font-weight:600}
.qn-badge{margin-left:auto;font-size:9px;padding:1px 6px;border:1px solid var(--accBd);border-radius:4px;color:var(--acc);font-weight:600}

.qp-add{padding:8px 12px;border-top:1px solid var(--b1);flex-shrink:0}

/* Main Content */
.mp-inner{padding:24px 28px;max-width:1100px}

/* Cards */
.card{background:var(--s1);border:1px solid var(--b1);border-radius:8px;margin-bottom:16px;box-shadow:0 1px 2px rgba(26,25,21,.04);overflow:hidden}
.card-hd{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;border-bottom:1px solid var(--b1)}
.card-title{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--acc);font-weight:700}
.card-body{padding:14px 16px}
.card-foot{padding:10px 16px;border-top:1px solid var(--b1);display:flex;justify-content:flex-end;gap:8px;background:var(--s2)}

/* Section headers */
.sec-hd{display:flex;align-items:center;gap:10px;margin-bottom:12px;margin-top:4px}
.sec-num{width:20px;height:20px;background:var(--acc);color:#fff;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;flex-shrink:0}
.sec-label{font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--acc);font-weight:700}
.sec-line{flex:1;height:1px;background:var(--b1)}

/* Form */
.field{display:flex;flex-direction:column;gap:4px;margin-bottom:12px}
.lbl{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--mu);font-weight:600}
.inp,.sel{background:#fff;border:1px solid var(--b2);border-radius:6px;color:var(--txb);font-family:var(--sans);font-size:12.5px;padding:7px 10px;width:100%;outline:none;transition:border .12s}
.inp:focus,.sel:focus{border-color:var(--acc)}
.inp::placeholder{color:var(--di)}
.inp-sm{padding:5px 8px;font-size:11.5px}

/* Buttons */
.btn{border:1px solid var(--b2);background:var(--s1);color:var(--tx);font-family:var(--sans);font-size:10.5px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;padding:7px 14px;border-radius:6px;cursor:pointer;transition:all .12s;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn:hover{border-color:var(--b3);background:var(--s3)}
.btn-acc{background:var(--acc);border-color:var(--acc);color:#fff}
.btn-acc:hover{background:var(--acc2);border-color:var(--acc2)}
.btn-ghost{background:none;border-color:var(--b2);color:var(--mu)}
.btn-ghost:hover{border-color:var(--b3);color:var(--tx);background:none}
.btn-danger{background:none;border-color:var(--redBd);color:var(--red)}
.btn-danger:hover{background:var(--redBg)}
.btn-gold{background:var(--gold);border-color:var(--gold);color:#fff}
.btn-sm{padding:5px 10px;font-size:9.5px}
.btn-xs{padding:3px 8px;font-size:9px}
.btn-block{width:100%;justify-content:center}

/* Property Picker */
.pp-search{margin-bottom:8px}
.pp-groups{display:flex;flex-wrap:wrap;gap:4px;margin-bottom:8px}
.pp-gtag{font-size:9.5px;padding:3px 9px;border:1px solid var(--b2);border-radius:99px;color:var(--mu);cursor:pointer;transition:all .1s;letter-spacing:.03em;font-weight:500}
.pp-gtag:hover{border-color:var(--b3)}
.pp-gtag.on{border-color:var(--accBd);color:var(--acc);background:var(--accBg)}
.pp-list{max-height:260px;overflow-y:auto;border:1px solid var(--b1);border-radius:6px;background:var(--s1)}
.pp-row{display:flex;align-items:center;gap:8px;padding:6px 10px;cursor:pointer;transition:background .08s;border-bottom:1px solid var(--b1)}
.pp-row:last-child{border-bottom:none}
.pp-row:hover{background:var(--s2)}
.pp-row.sel{background:var(--accBg)}
.pp-check{width:15px;height:15px;border:1px solid var(--b2);border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:9px;flex-shrink:0;color:transparent;transition:all .1s;background:#fff}
.pp-row.sel .pp-check{border-color:var(--acc);color:#fff;background:var(--acc)}
.pp-name{font-size:12px;font-weight:500;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pp-detail{font-size:10.5px;color:var(--mu);font-family:var(--mono);font-variant-numeric:tabular-nums}

/* Method Selector */
.ms-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px}
.ms-card{border:1px solid var(--b1);border-radius:6px;background:var(--s1);padding:8px 10px;cursor:pointer;transition:all .12s}
.ms-card:hover{border-color:var(--b3)}
.ms-card.on{border-color:var(--acc);background:var(--accBg)}
.ms-name{font-size:11.5px;font-weight:600;margin-bottom:1px;color:var(--txb)}
.ms-desc{font-size:9.5px;color:var(--mu);line-height:1.35}
.ms-tag{font-size:8px;padding:1px 5px;border:1px solid var(--b2);border-radius:3px;color:var(--mu);float:right;margin-left:6px}
.ms-card.on .ms-tag{border-color:var(--accBd);color:var(--acc)}

/* Manual % */
.mp-row{display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--b1)}
.mp-row:last-child{border-bottom:none}
.mp-inp{width:64px;background:#fff;border:1px solid var(--b2);border-radius:5px;color:var(--txb);font-family:var(--mono);font-size:11px;padding:3px 6px;text-align:right}

/* Post mode toggle */
.pm-row{display:flex;gap:4px;margin-bottom:10px}
.pm-opt{flex:1;border:1px solid var(--b1);border-radius:6px;padding:6px 8px;cursor:pointer;text-align:center;font-size:9.5px;font-weight:600;transition:all .1s;letter-spacing:.04em;text-transform:uppercase;color:var(--mu);background:var(--s1)}
.pm-opt:hover{border-color:var(--b3)}
.pm-opt.on{border-color:var(--acc);color:var(--acc);background:var(--accBg)}

/* JE Preview table */
.tbl{width:100%;border-collapse:collapse;font-size:12px}
.tbl th{border-bottom:1px solid var(--b2);padding:6px 9px;text-align:left;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--mu);background:var(--s2);white-space:nowrap;font-weight:600}
.tbl td{padding:6px 9px;border-bottom:1px solid var(--b1);vertical-align:middle;font-size:11.5px;font-variant-numeric:tabular-nums}
.tbl tr:last-child td{border-bottom:none}
.tbl tr:hover td{background:rgba(26,25,21,.02)}
.tbl-foot td{background:var(--s2)!important;font-weight:600;border-top:1px solid var(--b2)}
.dr{color:var(--acc)}.cr{color:var(--green)}
.indent{padding-left:18px!important;color:var(--mu);font-size:10.5px!important}

/* Balance indicator */
.bal{display:inline-flex;align-items:center;gap:5px;padding:4px 11px;border-radius:5px;font-size:9.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase}
.bal-ok{border:1px solid var(--greenBd);color:var(--green);background:var(--greenBg)}
.bal-err{border:1px solid var(--redBd);color:var(--red);background:var(--redBg)}

/* Badges */
.badge{font-size:8.5px;padding:2px 7px;border:1px solid;border-radius:4px;letter-spacing:.07em;text-transform:uppercase;white-space:nowrap;font-weight:600}
.b-green{border-color:var(--greenBd);color:var(--green)}
.b-gold{border-color:var(--goldBd);color:var(--gold)}
.b-acc{border-color:var(--accBd);color:var(--acc)}
.b-red{border-color:var(--redBd);color:var(--red)}
.b-dim{border-color:var(--b2);color:var(--mu)}

/* Stats strip */
.stats{display:flex;border:1px solid var(--b1);border-radius:8px;margin-bottom:20px;background:var(--s1);box-shadow:0 1px 2px rgba(26,25,21,.04);overflow:hidden}
.stat{flex:1;padding:14px 16px;border-right:1px solid var(--b1)}
.stat:last-child{border-right:none}
.stat-val{font-family:var(--head);font-size:24px;font-weight:900;color:var(--acc);letter-spacing:-.5px}
.stat-lbl{font-size:9px;color:var(--mu);letter-spacing:.1em;text-transform:uppercase;margin-top:2px;font-weight:600}

/* JE List items */
.je-item{border:1px solid var(--b1);border-radius:6px;margin-bottom:8px;background:var(--s1);overflow:hidden}
.je-hd{display:flex;align-items:center;gap:8px;padding:8px 14px;cursor:pointer;transition:background .08s}
.je-hd:hover{background:var(--s2)}
.je-expanded{border-top:1px solid var(--b1)}

/* Connector cards */
.conn-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:10px;margin-bottom:20px}
.conn-card{background:var(--s1);border:1px solid var(--b1);border-radius:8px;cursor:pointer;transition:all .12s;position:relative;overflow:hidden}
.conn-card:hover:not(.disabled){border-color:var(--b3);box-shadow:0 2px 8px rgba(26,25,21,.06)}
.conn-card.sel{border-color:var(--acc)}
.conn-card.disabled{opacity:.45;cursor:not-allowed}
.conn-top{padding:12px;display:flex;gap:10px;align-items:center}
.conn-logo{width:34px;height:34px;display:flex;align-items:center;justify-content:center;font-family:var(--head);font-weight:900;font-size:11px;flex-shrink:0;border:1px solid;border-radius:7px}
.conn-info{flex:1;min-width:0}
.conn-name{font-family:var(--head);font-weight:700;font-size:13px;color:var(--txb)}
.conn-cat{font-size:9px;color:var(--mu);letter-spacing:.06em;text-transform:uppercase}
.conn-status{position:absolute;top:8px;right:8px;font-size:8px;padding:1px 6px;border:1px solid;border-radius:3px;letter-spacing:.06em;text-transform:uppercase;font-weight:600}

/* Config panel */
.cpanel{border:1px solid var(--b1);border-radius:8px;background:var(--s1);margin-top:0;overflow:hidden}
.cpanel-hd{display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--b1)}
.cpanel-tabs{display:flex;border-bottom:1px solid var(--b1);padding:0 16px;gap:2px}
.cptab{background:none;border:none;color:var(--mu);font-family:var(--sans);font-size:10px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;padding:8px 12px;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;transition:all .12s}
.cptab:hover{color:var(--tx)}
.cptab.on{color:var(--acc);border-bottom-color:var(--acc)}
.cpanel-body{padding:16px}
.code-block{background:#23211B;border:1px solid #3A372E;border-radius:8px;padding:12px;font-family:var(--mono);font-size:10.5px;line-height:1.7;color:#E8DCC8;overflow-x:auto;white-space:pre}
.info-box{background:var(--accBg);border:1px solid var(--accBd);border-radius:8px;padding:9px 13px;margin-bottom:12px;font-size:11.5px;color:var(--acc2);line-height:1.5}
.test-res{padding:8px 12px;border:1px solid;border-radius:6px;font-size:11.5px;display:flex;align-items:center;gap:8px;margin-top:8px}

/* Admin Drawer Overlay */
.drawer-overlay{position:fixed;inset:0;background:rgba(26,25,21,.3);z-index:500;opacity:0;pointer-events:none;transition:opacity .2s}
.drawer-overlay.open{opacity:1;pointer-events:auto}
.drawer{position:fixed;top:0;right:0;bottom:0;width:680px;max-width:90vw;background:var(--s1);border-left:1px solid var(--b1);box-shadow:-8px 0 28px rgba(26,25,21,.1);z-index:501;transform:translateX(100%);transition:transform .25s ease;overflow-y:auto}
.drawer.open{transform:translateX(0)}
.drawer-hd{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-bottom:1px solid var(--b1);position:sticky;top:0;background:var(--s1);z-index:1}
.drawer-title{font-family:var(--head);font-size:17px;font-weight:700;color:var(--txb)}
.drawer-body{padding:20px}
.drawer-tabs{display:flex;gap:0;border-bottom:1px solid var(--b1);padding:0 20px;position:sticky;top:53px;background:var(--s1);z-index:1}
.dtab{background:none;border:none;color:var(--mu);font-family:var(--sans);font-size:10px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;padding:9px 14px;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;transition:all .12s}
.dtab:hover{color:var(--tx)}
.dtab.on{color:var(--acc);border-bottom-color:var(--acc)}

/* Confirm Modal */
.modal-overlay{position:fixed;inset:0;background:rgba(26,25,21,.38);z-index:600;display:flex;align-items:center;justify-content:center}
.modal{background:var(--s1);border:1px solid var(--b1);border-radius:10px;box-shadow:0 12px 40px rgba(26,25,21,.18);padding:20px 24px;max-width:400px;width:90%}
.modal-title{font-family:var(--head);font-size:15px;font-weight:700;margin-bottom:8px;color:var(--txb)}
.modal-text{font-size:12px;color:var(--mu);line-height:1.5;margin-bottom:16px}
.modal-actions{display:flex;gap:8px;justify-content:flex-end}

/* Flash */
.flash{position:fixed;bottom:16px;right:16px;background:var(--txb);border:1px solid var(--txb);border-radius:8px;color:var(--s1);padding:9px 15px;font-size:11.5px;font-weight:500;box-shadow:0 6px 20px rgba(26,25,21,.2);z-index:9999;animation:fu .2s ease}
@keyframes fu{from{transform:translateY(8px);opacity:0}to{transform:translateY(0);opacity:1}}

/* Empty */
.empty{text-align:center;padding:40px 20px;color:var(--mu)}
.empty-ico{font-size:24px;margin-bottom:8px;opacity:.5}

/* Group cards */
.grp-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}

/* Steps */
.step{display:flex;gap:10px;margin-bottom:10px}
.step-n{width:20px;height:20px;background:var(--acc);color:#fff;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;flex-shrink:0}
.step-t{font-size:12px;font-weight:600;margin-bottom:1px;color:var(--txb)}
.step-d{font-size:10.5px;color:var(--mu);line-height:1.4}

/* Scrollbar */
::-webkit-scrollbar{width:8px;height:8px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:var(--b2);border-radius:99px}
::-webkit-scrollbar-thumb:hover{background:var(--b3)}

/* Misc */
hr{border:none;border-top:1px solid var(--b1);margin:10px 0}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}
.scroll-x{overflow-x:auto}
.close-btn{background:none;border:1px solid var(--b2);border-radius:6px;color:var(--mu);width:26px;height:26px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:13px;transition:all .12s}
.close-btn:hover{border-color:var(--red);color:var(--red)}

/* Alloc workspace 2-col layout */
.ws-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;align-items:start}
.ws-full{grid-column:1/-1}

/* Add invoice mini form in queue */
.add-form{padding:10px 12px;border-top:1px solid var(--b1);background:var(--s2)}
.add-form .field{margin-bottom:8px}
.add-row{display:grid;grid-template-columns:1fr 1fr;gap:6px}

/* Property Recon Grid */
.pr-row{border:1px solid var(--b1);border-radius:6px;background:var(--s1);margin-bottom:3px;transition:all .12s;overflow:hidden}
.pr-row:hover{border-color:var(--b3)}
.pr-row.expanded{border-color:var(--accBd);margin-bottom:8px;box-shadow:0 2px 8px rgba(26,25,21,.06)}
.pr-header{display:grid;grid-template-columns:1fr 110px 110px 90px 110px 50px;align-items:center;padding:10px 16px;cursor:pointer;gap:8px}
.pr-name{font-size:12.5px;font-weight:600;color:var(--txb);display:flex;align-items:center;gap:8px}
.pr-name .pr-arrow{font-size:8px;color:var(--mu);transition:transform .15s;width:12px}
.pr-row.expanded .pr-arrow{transform:rotate(90deg);color:var(--acc)}
.pr-val{font-size:12.5px;font-weight:600;text-align:right;font-family:var(--mono);font-variant-numeric:tabular-nums}
.pr-pct{font-size:10.5px;color:var(--mu);text-align:right;font-family:var(--mono)}
.pr-bar{height:3px;background:var(--b1);margin:0 16px;border-radius:99px;overflow:hidden}
.pr-bar-fill{height:100%;background:var(--acc);transition:width .2s}
.pr-detail{border-top:1px solid var(--b1);padding:0}
.pr-detail .tbl td{font-size:11px}
.pr-detail .tbl th{font-size:8.5px}
.pr-search{background:#fff;border:1px solid var(--b2);border-radius:6px;color:var(--txb);font-family:var(--sans);font-size:11.5px;padding:6px 11px;width:220px;outline:none;transition:border .12s}
.pr-search:focus{border-color:var(--acc)}
.pr-search::placeholder{color:var(--di)}
.pr-totals{display:grid;grid-template-columns:1fr 110px 110px 90px 110px 50px;align-items:center;padding:9px 16px;gap:8px;border-top:2px solid var(--b2);background:var(--s2);font-weight:700;border-radius:0 0 6px 6px}

/* Responsive */
@media(max-width:900px){
  .shell{grid-template-columns:1fr}
  .queue-panel{display:none}
  .ws-grid{grid-template-columns:1fr}
  .ms-grid{grid-template-columns:1fr}
  .grp-grid{grid-template-columns:1fr}
  .stats{flex-wrap:wrap}
}

/* Consolidation / Elim Check */
.elim-tabs{display:flex;gap:0;border-bottom:1px solid var(--b1);margin-bottom:18px}
.elim-tab{background:none;border:none;border-bottom:2px solid transparent;color:var(--mu);font-family:var(--sans);font-size:10px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;padding:8px 16px;cursor:pointer;margin-bottom:-1px;transition:all .12s}
.elim-tab:hover{color:var(--tx)}
.elim-tab.on{color:var(--acc);border-bottom-color:var(--acc)}
.elim-tbl{width:100%;border-collapse:collapse;font-size:12px}
.elim-tbl th{background:var(--s2);padding:6px 10px;text-align:left;font-size:8.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--mu);white-space:nowrap;border-bottom:1px solid var(--b2);font-weight:600}
.elim-tbl th.r{text-align:right}
.elim-tbl td{padding:7px 10px;border-bottom:1px solid var(--b1);font-variant-numeric:tabular-nums;vertical-align:middle}
.elim-tbl td.r{text-align:right;font-family:var(--mono);font-size:11.5px}
.elim-tbl tr:hover td{background:rgba(26,25,21,.02)}
.elim-tbl tr.ok-row td{}
.elim-tbl tr.bad-row td{background:rgba(179,56,44,.05)}
.elim-status{display:inline-block;font-size:8px;padding:2px 7px;border:1px solid;border-radius:4px;letter-spacing:.07em;text-transform:uppercase;white-space:nowrap;font-weight:600}
.es-ok{border-color:var(--greenBd);color:var(--green)}
.es-bad{border-color:var(--redBd);color:var(--red)}
.es-none{border-color:var(--b2);color:var(--mu)}
.es-warn{border-color:var(--goldBd);color:var(--gold)}
.matrix-grid{display:grid;border:1px solid var(--b1);border-radius:8px;background:var(--s1);margin-bottom:16px;overflow-x:auto}
.matrix-hdr{font-size:9px;letter-spacing:.09em;text-transform:uppercase;color:var(--mu);padding:6px 10px;background:var(--s2);border-bottom:1px solid var(--b1);white-space:nowrap;font-weight:600}
.matrix-row{display:contents}
.matrix-cell{padding:8px 10px;border-bottom:1px solid var(--b1);border-right:1px solid var(--b1);font-size:11px;font-family:var(--mono);font-variant-numeric:tabular-nums;white-space:nowrap}
.mc-pos{color:var(--acc);font-weight:600}
.mc-neg{color:var(--red);font-weight:600}
.mc-zero{color:var(--di)}
.mc-label{color:var(--tx);font-weight:500;background:var(--s1);font-family:var(--sans)}
.mc-diag{color:var(--di);text-align:center}
.import-drop{border:1px dashed var(--b3);border-radius:8px;padding:24px;text-align:center;cursor:pointer;transition:all .15s;margin-bottom:12px;background:var(--s1)}
.import-drop:hover{border-color:var(--acc);background:var(--accBg)}
.approve-banner{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border:1px solid var(--greenBd);border-radius:8px;background:var(--greenBg);margin-bottom:16px}
.import-badge{font-size:8px;padding:1px 6px;border:1px solid var(--accBd);border-radius:3px;color:var(--acc);margin-left:6px;letter-spacing:.06em;text-transform:uppercase;font-weight:600}
`;

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function IntercoIQ({ auth }) {
  const { profile, org, signOut } = auth;

  // Core data (hydrated from Supabase)
  const [properties, setProperties] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [glRows, setGlRows] = useState([]);
  const [glTxns, setGlTxns] = useState([]);
  const [methods, setMethods] = useState([]);
  const [groups, setGroups] = useState([]);
  const [journals, setJournals] = useState([]);
  const [watchedGLs, setWatchedGLs] = useState([]);
  const [connectedSystems, setConnectedSystems] = useState(new Set());
  const [templates, setTemplates] = useState([]);
  const [mgmtName, setMgmtName] = useState("");
  const [loaded, setLoaded] = useState(false);

  // UI state
  const [flash, setFlash] = useState(null);
  const [activeQueueItem, setActiveQueueItem] = useState(null); // txn id or "manual"
  const [rightPane, setRightPane] = useState("dashboard"); // dashboard | allocate | journal | templates
  const [expandedJE, setExpandedJE] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // {title, text, onConfirm}
  const [propSearch, setPropSearch] = useState("");

  // Property recon state
  const [expandedProp, setExpandedProp] = useState(null);
  const [propFilter, setPropFilter] = useState("");

  // Consolidation Readiness state
  const [consolidationTab, setConsolidationTab] = useState("matrix"); // matrix | elim
  const [glImportRows, setGlImportRows] = useState([]);    // [{propId, dueTo, dueFrom}]
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCsvText, setImportCsvText] = useState("");
  const [importPreview, setImportPreview] = useState([]);
  const [importError, setImportError] = useState("");
  const [periodApproved, setPeriodApproved] = useState(false);
  const [approvedAt, setApprovedAt] = useState(null);

  // Admin drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("connectors"); // connectors | groups | methods | watchlist | settings

  // Connector state
  const [selConnector, setSelConnector] = useState(null);
  const [connTab, setConnTab] = useState("config");
  const [testState, setTestState] = useState(null);
  const [connCreds, setConnCreds] = useState({});
  const [connCat, setConnCat] = useState("All");

  // Group form
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [newGroup, setNewGroup] = useState({name:"",filters:{}});

  // Method form
  const [showMethodForm, setShowMethodForm] = useState(false);
  const [newMethod, setNewMethod] = useState({name:"",description:"",driver:"equal"});

  // Manual queue add form
  const [addForm, setAddForm] = useState({vendor:"",amount:"",gl:"",date:new Date().toISOString().slice(0,10),memo:""});

  // Allocation form
  const blank = {
    vendor:"", invoiceNo:"", date:new Date().toISOString().slice(0,10),
    amount:"", description:"", memo:"",
    expenseGl:"", icCreditGl:"", icDebitGl:"",
    selectedProps:[], activeGroup:"",
    methodId:"",
    postMode:"both",
    sourceTxnId:"",
  };
  const [inv, setInv] = useState(blank);
  const [manualPcts, setManualPcts] = useState({});
  const sf = (k,v) => setInv(p=>({...p,[k]:v}));

  const selMethod = methods.find(m=>m.id===inv.methodId);
  const selProps = properties.filter(p=>inv.selectedProps.includes(p.id));

  const filteredProps = useMemo(()=>{
    let result = properties;
    if (inv.activeGroup) {
      const g = groups.find(x=>x.id===inv.activeGroup);
      if (g) result = result.filter(p=>Object.entries(g.filters).every(([k,v])=>p[k]===v));
    }
    if (propSearch.trim()) {
      const q = propSearch.toLowerCase();
      result = result.filter(p=> p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q));
    }
    return result;
  },[inv.activeGroup, groups, properties, propSearch]);

  const allocLines = useMemo(()=>
    selMethod ? computeLines(inv.amount, selMethod, selProps, manualPcts) : [],
    [inv.amount, selMethod, selProps, manualPcts]
  );
  const mPctTotal = inv.selectedProps.reduce((a,id)=>a+parseFloat(manualPcts[id]||0),0);
  const expGl  = glAccounts.find(g=>g.code===inv.expenseGl);
  const icCrGl = glAccounts.find(g=>g.code===inv.icCreditGl);
  const icDrGl = glAccounts.find(g=>g.code===inv.icDebitGl);

  const pendingTxns = glTxns.filter(t=>watchedGLs.includes(t.gl)&&!t.allocated);
  const allocatedTxns = glTxns.filter(t=>watchedGLs.includes(t.gl)&&t.allocated);
  const totalPosted = journals.filter(j=>j.status==="posted").reduce((a,j)=>a+j.amount,0);

  // ── Property Reconciliation Data (GL-aware with IC balance matching) ────────
  const propertyRecon = useMemo(() => {
    const propMap = {};
    // Initialize all properties with zero values
    properties.forEach(p => {
      propMap[p.id] = {
        id: p.id, name: p.name, units: p.units, region: p.region, division: p.division,
        expenseTotal: 0,    // Dr Expense booked to this property
        icDueTo: 0,         // Cr Due To Mgmt Co (property side)
        icDueFrom: 0,       // Dr Due From Property (mgmt co side)
        jeCount: 0, jes: [], lastPosted: null,
        glBreakdown: {},     // { glCode: { name, expense, dueTo, dueFrom } }
        mismatchJEs: [],     // JEs where one side is missing
      };
    });

    // Aggregate from posted journals
    journals.filter(j => j.status === "posted").forEach(je => {
      je.lines.forEach(line => {
        const pid = line.property.id;
        if (!propMap[pid]) return;
        const amt = line.amount;
        const expCode = je.expenseGl?.code || "?";
        const expName = je.expenseGl?.name || "";

        // Initialize GL breakdown entry
        if (!propMap[pid].glBreakdown[expCode]) {
          propMap[pid].glBreakdown[expCode] = { name: expName, expense: 0, dueTo: 0, dueFrom: 0 };
        }

        // Property side: Dr Expense / Cr IC Due To
        if (je.postMode === "both" || je.postMode === "property_only") {
          propMap[pid].expenseTotal += amt;
          propMap[pid].icDueTo += amt;
          propMap[pid].glBreakdown[expCode].expense += amt;
          propMap[pid].glBreakdown[expCode].dueTo += amt;
        }

        // Mgmt Co side: Dr IC Due From (attributed back to property)
        if (je.postMode === "both" || je.postMode === "mgmt_only") {
          propMap[pid].icDueFrom += amt;
          propMap[pid].glBreakdown[expCode].dueFrom += amt;
          // If mgmt_only, expense wasn't booked on property side
          if (je.postMode === "mgmt_only") {
            propMap[pid].glBreakdown[expCode].expense += 0; // explicit zero
          }
        }

        // Track mismatch JEs (one-sided postings)
        if (je.postMode === "property_only" || je.postMode === "mgmt_only") {
          if (!propMap[pid].mismatchJEs.find(j => j.id === je.id)) {
            propMap[pid].mismatchJEs.push(je);
          }
        }

        // Track unique JEs per property
        if (!propMap[pid].jes.find(j => j.id === je.id)) {
          propMap[pid].jes.push(je);
          propMap[pid].jeCount += 1;
          if (!propMap[pid].lastPosted || je.postedAt > propMap[pid].lastPosted) {
            propMap[pid].lastPosted = je.postedAt;
          }
        }
      });
    });

    return Object.values(propMap)
      .map(p => ({ ...p, variance: +(p.icDueTo - p.icDueFrom).toFixed(2), hasMismatch: Math.abs(p.icDueTo - p.icDueFrom) >= 0.01 }))
      .sort((a, b) => b.expenseTotal - a.expenseTotal);
  }, [journals, properties]);

  const filteredRecon = useMemo(() => {
    if (!propFilter.trim()) return propertyRecon;
    const q = propFilter.toLowerCase();
    return propertyRecon.filter(p => p.name.toLowerCase().includes(q));
  }, [propertyRecon, propFilter]);

  // ── Elimination Check Pairs ───────────────────────────────────────────────────
  // Each row = one entity pair (Property ↔ Mgmt Co).
  // propBalance = what the property's books show as Due-To (liability, sign-flipped for elim)
  // mgmtBalance = what mgmt books show as Due-From (asset)
  // Difference should = 0 for elimination to work.
  const elimPairs = useMemo(() => {
    return propertyRecon.map(p => {
      const importRow = glImportRows.find(r => r.propId === p.id);
      const iqDueTo   = p.icDueTo;    // IntercoIQ: Cr Due-To on property books
      const iqDueFrom = p.icDueFrom;  // IntercoIQ: Dr Due-From on mgmt books
      const iqDiff    = +(iqDueTo - iqDueFrom).toFixed(2);
      const glDueTo   = importRow ? importRow.dueTo   : null;
      const glDueFrom = importRow ? importRow.dueFrom : null;
      const glDiff    = glDueTo != null ? +(glDueTo - glDueFrom).toFixed(2) : null;
      // GL vs IQ discrepancy for each side
      const glIqDueToDisc   = glDueTo   != null ? +(glDueTo   - iqDueTo  ).toFixed(2) : null;
      const glIqDueFromDisc = glDueFrom != null ? +(glDueFrom - iqDueFrom).toFixed(2) : null;
      const iqStatus   = iqDueTo===0&&iqDueFrom===0 ? "none" : Math.abs(iqDiff)<0.01 ? "ok" : "mismatch";
      const glStatus   = glDiff===null ? "no_import" : Math.abs(glDiff)<0.01 ? "ok" : "mismatch";
      const discStatus = (glIqDueToDisc!==null&&Math.abs(glIqDueToDisc)>=0.01) ||
                         (glIqDueFromDisc!==null&&Math.abs(glIqDueFromDisc)>=0.01) ? "discrepancy" : "match";
      return { ...p, iqDueTo, iqDueFrom, iqDiff, iqStatus,
               glDueTo, glDueFrom, glDiff, glStatus,
               glIqDueToDisc, glIqDueFromDisc, discStatus };
    });
  }, [propertyRecon, glImportRows]);

  const totalExpense = propertyRecon.reduce((a, p) => a + p.expenseTotal, 0);
  const totalDueTo = propertyRecon.reduce((a, p) => a + p.icDueTo, 0);
  const totalDueFrom = propertyRecon.reduce((a, p) => a + p.icDueFrom, 0);
  const totalVariance = +(totalDueTo - totalDueFrom).toFixed(2);
  const totalJECount = journals.filter(j => j.status === "posted").length;
  const propertiesWithAllocations = propertyRecon.filter(p => p.jeCount > 0).length;
  const mismatchCount = propertyRecon.filter(p => p.hasMismatch).length;

  function msg(m){setFlash(m);setTimeout(()=>setFlash(null),2800)}

  // ── Data loading (Supabase → the client shapes the UI already uses) ───────
  const CUR_PERIOD = `${new Date().toISOString().slice(0, 7)}-01`;
  const glIdByCode = (code) => glRows.find((g) => g.code === code)?.id || null;
  const ERP_DB_ID = { qbe: "quickbooks" };
  const ERP_UI_ID = { quickbooks: "qbe" };

  async function loadAll() {
    const [ents, gls, txns, mths, grps, jes, tpls, bals, appr, conns] = await Promise.all([
      supabase.from("entities").select("*").eq("org_id", org.id).order("name"),
      supabase.from("gl_accounts").select("*").eq("org_id", org.id).order("code"),
      supabase.from("ic_transactions").select("*").eq("org_id", org.id).order("txn_date", { ascending: false }),
      supabase.from("alloc_methods").select("*").eq("org_id", org.id).order("created_at"),
      supabase.from("entity_groups").select("*").eq("org_id", org.id).order("name"),
      supabase.from("ic_journals").select("*, ic_journal_lines(*)").eq("org_id", org.id).order("created_at", { ascending: false }),
      supabase.from("ic_templates").select("*").eq("org_id", org.id).order("created_at", { ascending: false }),
      supabase.from("ic_gl_balances").select("*").eq("org_id", org.id).eq("period", CUR_PERIOD),
      supabase.from("period_approvals").select("*").eq("org_id", org.id).eq("module", "intercoiq").eq("period", CUR_PERIOD),
      supabase.from("erp_connections").select("*").eq("org_id", org.id),
    ]);
    const err = [ents, gls, txns, mths, grps, jes, tpls, bals, appr, conns].find((r) => r.error)?.error;
    if (err) { msg(`Load failed: ${err.message}`); return; }
    const catMap = { expense: "expense", ic_due_to: "ic", ic_due_from: "ic", accounts_payable: "ap", prepaid_asset: "prepaid", other: "other" };
    const props = ents.data.filter((e) => e.entity_type !== "corporate").map((e) => ({
      id: e.id, name: e.name, units: e.units || 0, employees: e.employees || 0,
      region: e.region || "—", division: e.division || "—", ownership: e.ownership || "—",
    }));
    const propById = Object.fromEntries(props.map((p) => [p.id, p]));
    const glById = Object.fromEntries(gls.data.map((g) => [g.id, g]));
    const glObj = (id) => { const g = glById[id]; return g ? { code: g.code, name: g.name } : { code: "—", name: "—" }; };
    const methodById = Object.fromEntries(mths.data.map((m) => [m.id, m]));
    setMgmtName(ents.data.find((e) => e.entity_type === "corporate")?.name || org.name);
    setProperties(props);
    setGlRows(gls.data);
    setGlAccounts(gls.data.map((g) => ({ code: g.code, name: g.name, entity: catMap[g.category] || "other" })));
    setGlTxns(txns.data.map((t) => ({ id: t.id, date: t.txn_date, vendor: t.vendor, gl: glById[t.gl_id]?.code || "", amount: +t.amount, memo: t.memo || "", allocated: t.status === "allocated" })));
    setMethods(mths.data.map((m) => ({ id: m.id, name: m.name, description: m.description || "", driver: m.driver, custom: m.is_custom })));
    setGroups(grps.data.map((g) => ({ id: g.id, name: g.name, filters: g.filters || {} })));
    setJournals(jes.data.map((j) => {
      const m = methodById[j.method_id];
      return {
        id: j.id, vendor: j.vendor, invoiceNo: j.invoice_no || "", date: j.je_date, amount: +j.amount,
        description: j.description || "", memo: j.memo_tag,
        expenseGl: glObj(j.expense_gl_id), icCreditGl: glObj(j.ic_credit_gl_id), icDebitGl: glObj(j.ic_debit_gl_id),
        method: m ? { id: m.id, name: m.name, driver: m.driver } : { id: null, name: "—", driver: "equal" },
        lines: (j.ic_journal_lines || []).map((l) => ({ property: propById[l.entity_id] || { id: l.entity_id, name: "(removed)", units: 0, employees: 0 }, amount: +l.amount, pct: l.pct == null ? 0 : +l.pct })),
        postMode: j.post_mode === "entity_only" ? "property_only" : j.post_mode,
        sourceTxnId: j.source_txn_id, allocationKey: j.allocation_key, status: j.status,
        postedAt: j.posted_at, tag: j.memo_tag,
      };
    }));
    setTemplates(tpls.data.map((t) => ({ id: t.id, name: t.name, vendor: t.vendor, ...(t.config || {}) })));
    setGlImportRows(bals.data.map((b) => ({ propId: b.entity_id, dueTo: +b.due_to, dueFrom: +b.due_from })));
    setPeriodApproved(appr.data.length > 0);
    setApprovedAt(appr.data[0]?.approved_at || null);
    setWatchedGLs(org.settings?.watched_gls || []);
    setConnectedSystems(new Set(conns.data.filter((c) => c.status === "connected").map((c) => ERP_UI_ID[c.erp] || c.erp)));
    setLoaded(true);
  }
  useEffect(() => { loadAll(); }, [org.id]);

  async function saveWatchedGLs(next) {
    setWatchedGLs(next);
    const settings = { ...(org.settings || {}), watched_gls: next };
    const { error } = await supabase.from("organizations").update({ settings }).eq("id", org.id);
    if (error) msg(`Could not save watchlist: ${error.message}`);
    else { org.settings = settings; msg("Watchlist updated"); }
  }

  // ── Entrata JE export (Entrata's Import Journal Entries format) ───────────
  // Property side: Dr expense / Cr Due-To at each property.
  // Mgmt side: Dr Due-From per property + one Cr expense total, at the
  // corporate entity. Each property balances, so no Inter-Company flag needed.
  function entrataLinesForJournal(je) {
    const lines = [];
    const propSide = je.postMode === "both" || je.postMode === "property_only";
    const mgmtSide = je.postMode === "both" || je.postMode === "mgmt_only";
    const desc = `${je.description || je.vendor} ${je.tag || "[ICPIQ]"}`.trim();
    je.lines.forEach((l) => {
      if (propSide) {
        lines.push({ property: l.property.name, account: je.expenseGl?.code, description: desc, debit: l.amount, credit: null });
        lines.push({ property: l.property.name, account: je.icCreditGl?.code, description: `Due to ${mgmtName} — ${desc}`, debit: null, credit: l.amount });
      }
      if (mgmtSide) {
        lines.push({ property: mgmtName, account: je.icDebitGl?.code, description: `Due from ${l.property.name} — ${desc}`, debit: l.amount, credit: null });
      }
    });
    if (mgmtSide) {
      const total = je.lines.reduce((a, l) => a + l.amount, 0);
      lines.push({ property: mgmtName, account: je.expenseGl?.code, description: `Allocated out — ${desc}`, debit: null, credit: +total.toFixed(2) });
    }
    return lines;
  }

  function journalToEntrata(je) {
    return {
      reference: `ICPIQ-${String(je.id).slice(0, 6).toUpperCase()}`,
      memo: `${je.description || je.vendor} — IntercoIQ allocation`,
      postMonth: String(je.date).slice(0, 7),
      transactionDate: je.date,
      lines: entrataLinesForJournal(je),
    };
  }

  function exportJournalEntrata(je) {
    downloadEntrataCsv(`IntercoIQ_Entrata_${(je.vendor || "JE").replace(/\W+/g, "_")}_${je.date}.csv`, [journalToEntrata(je)]);
    msg("Entrata file downloaded");
  }

  function exportMonthEntrata() {
    const month = CUR_PERIOD.slice(0, 7);
    const posted = journals.filter((j) => j.status === "posted" && String(j.date).slice(0, 7) === month);
    if (!posted.length) { msg("No posted journals this month to export"); return; }
    downloadEntrataCsv(`IntercoIQ_Entrata_JEs_${month}.csv`, posted.map(journalToEntrata));
    msg(`Entrata file downloaded — ${posted.length} journal ${posted.length === 1 ? "entry" : "entries"}`);
  }

  async function setConnectorStatus(connId, connName, status) {
    const erp = ERP_DB_ID[connId] || connId;
    const { data: ex } = await supabase.from("erp_connections").select("id").eq("org_id", org.id).eq("erp", erp).maybeSingle();
    if (ex) await supabase.from("erp_connections").update({ status }).eq("id", ex.id);
    else if (status === "connected") await supabase.from("erp_connections").insert({ org_id: org.id, erp, label: connName, status });
    await loadAll();
  }

  function loadTxn(txn){
    setInv({...blank,
      sourceTxnId:txn.id, vendor:txn.vendor,
      amount:txn.amount.toString(), description:txn.memo,
      expenseGl:txn.gl, date:txn.date,
    });
    setManualPcts({});
    setPropSearch("");
    setActiveQueueItem(txn.id);
    setRightPane("allocate");
  }

  function startManualAllocation(){
    setInv({...blank});
    setManualPcts({});
    setPropSearch("");
    setActiveQueueItem("manual");
    setRightPane("allocate");
  }

  async function addManualToQueue(){
    if (!addForm.vendor || !addForm.amount) return;
    const { error } = await supabase.from("ic_transactions").insert({
      org_id: org.id, txn_date: addForm.date, vendor: addForm.vendor,
      gl_id: glIdByCode(addForm.gl), amount: parseFloat(addForm.amount), memo: addForm.memo || null, source: "manual",
    });
    if (error) { msg(`Could not add: ${error.message}`); return; }
    setAddForm({vendor:"",amount:"",gl:"",date:new Date().toISOString().slice(0,10),memo:""});
    setShowAddForm(false);
    msg(`+ ${addForm.vendor} added to queue`);
    await loadAll();
  }

  function toggleProp(id){
    setInv(p=>({...p, selectedProps:p.selectedProps.includes(id)
      ? p.selectedProps.filter(x=>x!==id)
      : [...p.selectedProps, id]}));
  }

  function selectGroup(gid){
    const g = groups.find(x=>x.id===gid);
    if (!g) return;
    const ids = properties.filter(p=>Object.entries(g.filters).every(([k,v])=>p[k]===v)).map(p=>p.id);
    setInv(p=>({...p, selectedProps:ids, activeGroup: p.activeGroup===gid ? "" : gid}));
    if (inv.activeGroup === gid) {
      setInv(p=>({...p, selectedProps:[], activeGroup:""}));
    }
  }

  async function handlePost(){
    if (!inv.vendor||!inv.amount||!inv.expenseGl||!inv.methodId||!inv.selectedProps.length){msg("Fill all required fields");return}
    if ((inv.postMode==="both"||inv.postMode==="property_only")&&!inv.icCreditGl){msg("Select IC Credit GL");return}
    if ((inv.postMode==="both"||inv.postMode==="mgmt_only")&&!inv.icDebitGl){msg("Select IC Debit GL");return}
    if (selMethod?.driver==="manual"&&Math.abs(mPctTotal-100)>0.01){msg("Manual %s must sum to 100%");return}

    // Idempotency check — prevent accidental duplicate posts (double-clicks, retries).
    // Key: vendor::invoiceNo::date::methodId. Reversed JEs do NOT block re-posts.
    const allocationKey = [
      (inv.vendor||"").trim().toLowerCase(),
      (inv.invoiceNo||"").trim().toLowerCase() || "no-inv",
      inv.date,
      inv.methodId,
    ].join("::");
    const existing = journals.find(j => j.allocationKey === allocationKey && j.status === "posted");
    if (existing) {
      msg(`Duplicate — ${existing.id} already posted for this vendor/invoice/date/method. Reverse it first to re-post.`);
      return;
    }

    const pmDb = inv.postMode === "property_only" ? "entity_only" : inv.postMode;
    const { data: je, error } = await supabase.from("ic_journals").insert({
      org_id: org.id, vendor: inv.vendor, invoice_no: inv.invoiceNo || null, je_date: inv.date,
      amount: parseFloat(inv.amount), description: inv.description || null, memo_tag: "[ICPIQ]",
      expense_gl_id: glIdByCode(inv.expenseGl), ic_credit_gl_id: glIdByCode(inv.icCreditGl), ic_debit_gl_id: glIdByCode(inv.icDebitGl),
      method_id: inv.methodId, post_mode: pmDb, source_txn_id: inv.sourceTxnId || null,
      allocation_key: allocationKey, status: "posted", posted_by: profile.id, posted_at: new Date().toISOString(),
    }).select("id").single();
    if (error) {
      msg(error.code === "23505" ? "Duplicate — the database blocked a second posting of this vendor/invoice/date/method." : `Post failed: ${error.message}`);
      return;
    }
    const { error: lineErr } = await supabase.from("ic_journal_lines").insert(allocLines.map((l) => ({
      org_id: org.id, journal_id: je.id, entity_id: l.property.id, amount: l.amount, pct: +l.pct.toFixed(4),
    })));
    if (lineErr) { msg(`Line save failed: ${lineErr.message}`); return; }
    if (inv.sourceTxnId) await supabase.from("ic_transactions").update({ status: "allocated" }).eq("id", inv.sourceTxnId);
    await supabase.from("audit_log").insert({ org_id: org.id, module: "intercoiq", action: "POST", ref: je.id, actor_id: profile.id, note: `${inv.vendor} ${inv.invoiceNo || ""} · ${fmt(parseFloat(inv.amount))} · ${allocLines.length} lines` });
    setInv(blank); setManualPcts({}); setPropSearch("");
    setActiveQueueItem(null);
    setRightPane("journal");
    msg(`Journal posted — ${allocLines.length} entity lines`);
    await loadAll();
  }

  function handleReverse(id){
    setConfirmModal({
      title:"Reverse Journal Entry",
      text:`This will reverse ${id}. Per the append-only ledger, the original entry remains in the log with a "reversed" status. This cannot be undone.`,
      onConfirm: async ()=>{
        const { error } = await supabase.from("ic_journals").update({ status: "reversed" }).eq("id", id);
        if (error) { msg(`Reverse failed: ${error.message}`); return; }
        await supabase.from("audit_log").insert({ org_id: org.id, module: "intercoiq", action: "REVERSE", ref: id, actor_id: profile.id, note: "Journal reversed" });
        setConfirmModal(null);
        msg("Journal reversed");
        await loadAll();
      }
    });
  }

  async function saveAsTemplate(){
    if (!inv.vendor || !inv.methodId || !inv.selectedProps.length) { msg("Fill vendor, method, and entities first"); return; }
    const { error } = await supabase.from("ic_templates").insert({
      org_id: org.id, name: inv.description || inv.vendor, vendor: inv.vendor,
      config: { expenseGl: inv.expenseGl, icCreditGl: inv.icCreditGl, icDebitGl: inv.icDebitGl, methodId: inv.methodId, selectedProps: [...inv.selectedProps], postMode: inv.postMode, manualPcts: { ...manualPcts } },
    });
    if (error) { msg(error.code === "23505" ? "A template with this name already exists" : `Template save failed: ${error.message}`); return; }
    msg(`Template "${inv.description || inv.vendor}" saved`);
    await loadAll();
  }

  function loadTemplate(t){
    setInv(p=>({...blank,
      vendor:t.vendor, expenseGl:t.expenseGl,
      icCreditGl:t.icCreditGl, icDebitGl:t.icDebitGl,
      methodId:t.methodId, selectedProps:t.selectedProps,
      postMode:t.postMode, date:new Date().toISOString().slice(0,10),
    }));
    setManualPcts(t.manualPcts||{});
    setActiveQueueItem("manual");
    setRightPane("allocate");
    msg(`Template loaded — enter amount`);
  }

  const CONN_CATS = ["All",...new Set(CONNECTORS.map(c=>c.category))];
  const filteredConns = CONNECTORS.filter(c=>connCat==="All"||c.category===connCat);
  const STATUS_COLOR = {ga:"var(--green)", beta:"var(--gold)", roadmap:"var(--mu)"};
  const STATUS_LABEL = {ga:"Available", beta:"Beta", roadmap:"Roadmap"};

  // Debit/credit totals for balance check
  const previewDebitTotal = allocLines.reduce((a,l)=>a+l.amount,0) * (inv.postMode==="both"?2:1);
  const previewCreditTotal = previewDebitTotal;
  const isBalanced = allocLines.length > 0 && Math.abs(previewDebitTotal - previewCreditTotal) < 0.01;

  if (!loaded) {
    return (
      <>
        <style>{CSS}</style>
        <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", color: "var(--mu)", fontSize: 13 }}>
          Loading your workspace…
        </div>
      </>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="shell">

        {/* ════════ HEADER ════════ */}
        <header className="hdr">
          <div className="logo" title="Home" onClick={()=>{setRightPane("dashboard");setActiveQueueItem(null);}}>Interco<em>IQ</em></div>
          <div className="mod-switch" title="PIQ Platform modules">
            <a className="mod" href="https://www.amortiq.io">AmortIQ</a>
            <span className="mod on">IntercoIQ</span>
          </div>
          <div className="hdr-pills">
            {pendingTxns.length>0 && <span className="hpill hpill-gold">{pendingTxns.length} pending</span>}
            <span className="hpill hpill-green">{connectedSystems.size} connected</span>
            <span className="hpill" style={{borderColor:"var(--b2)",color:"var(--mu)",textTransform:"none",letterSpacing:0}} title={profile.email}>{org.name}</span>
            <button className="btn btn-ghost btn-sm" onClick={signOut}>Sign out</button>
            <button className="hdr-gear" onClick={()=>{setDrawerOpen(true);setSelConnector(null)}} title="Admin & Configuration">⚙</button>
          </div>
        </header>

        {/* ════════ LEFT QUEUE PANEL ════════ */}
        <div className="queue-panel">
          <div className="qp-head">
            <div style={{display:"flex",alignItems:"center"}}>
              <span className="qp-title">Allocation Queue</span>
              {pendingTxns.length>0 && <span className="qp-count">{pendingTxns.length}</span>}
            </div>
            <button className="btn btn-xs btn-ghost" onClick={()=>setShowAddForm(v=>!v)}>{showAddForm?"Cancel":"+ Add"}</button>
          </div>

          {showAddForm && (
            <div className="add-form">
              <div className="field"><label className="lbl">Vendor</label><input className="inp inp-sm" value={addForm.vendor} onChange={e=>setAddForm(p=>({...p,vendor:e.target.value}))} placeholder="Vendor name"/></div>
              <div className="add-row">
                <div className="field"><label className="lbl">Amount</label><input className="inp inp-sm" type="number" value={addForm.amount} onChange={e=>setAddForm(p=>({...p,amount:e.target.value}))} placeholder="0.00"/></div>
                <div className="field"><label className="lbl">GL</label>
                  <select className="sel" style={{padding:"5px 7px",fontSize:10}} value={addForm.gl} onChange={e=>setAddForm(p=>({...p,gl:e.target.value}))}>
                    <option value="">—</option>
                    {glAccounts.filter(g=>g.entity==="expense").map(g=><option key={g.code} value={g.code}>{g.code}</option>)}
                  </select>
                </div>
              </div>
              <div className="field"><label className="lbl">Memo</label><input className="inp inp-sm" value={addForm.memo} onChange={e=>setAddForm(p=>({...p,memo:e.target.value}))} placeholder="Optional"/></div>
              <button className="btn btn-acc btn-sm btn-block" onClick={addManualToQueue}>Add to Queue</button>
            </div>
          )}

          <div className="qp-list">
            {pendingTxns.length === 0 && allocatedTxns.length === 0 && (
              <div className="empty" style={{padding:30}}>
                <div className="empty-ico">◎</div>
                <div style={{fontSize:10}}>No items in queue</div>
                <div style={{fontSize:9,marginTop:4}}>Add watched GLs in Admin</div>
              </div>
            )}
            {pendingTxns.map(txn=>{
              const gl = glAccounts.find(g=>g.code===txn.gl);
              return (
                <div key={txn.id} className={`qi ${activeQueueItem===txn.id?"active":""}`} onClick={()=>loadTxn(txn)}>
                  <div className="qi-top">
                    <span className="qi-vendor">{txn.vendor}</span>
                    <span className="qi-amt">{fmt(txn.amount)}</span>
                  </div>
                  <div className="qi-meta">
                    <span>{txn.date}</span>
                    <span>{txn.gl} {gl?.name?.split(" ")[0]}</span>
                  </div>
                </div>
              );
            })}
            {allocatedTxns.length > 0 && (
              <>
                <div className="qp-section">Allocated</div>
                {allocatedTxns.map(txn=>(
                  <div key={txn.id} className="qi allocated">
                    <div className="qi-top">
                      <span className="qi-vendor">{txn.vendor}</span>
                      <span className="qi-amt">{fmt(txn.amount)}</span>
                    </div>
                    <div className="qi-meta">
                      <span>{txn.date}</span>
                      <span className="qi-badge b-green">Done</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="qp-nav">
            <button className={`qn-btn ${rightPane==="dashboard"&&!activeQueueItem?"on":""}`}
              onClick={()=>{setRightPane("dashboard");setActiveQueueItem(null)}}>
              <span style={{width:14,textAlign:"center",fontSize:12}}>⬡</span> Dashboard
            </button>
            <button className={`qn-btn ${rightPane==="journal"?"on":""}`}
              onClick={()=>{setRightPane("journal");setActiveQueueItem(null)}}>
              <span style={{width:14,textAlign:"center",fontSize:12}}>≡</span> Journal Log
              {journals.length>0 && <span className="qn-badge">{journals.length}</span>}
            </button>
            <button className={`qn-btn ${rightPane==="consolidation"?"on":""}`}
              onClick={()=>{setRightPane("consolidation");setActiveQueueItem(null)}}>
              <span style={{width:14,textAlign:"center",fontSize:12}}>⊞</span> Consolidation
              {elimPairs.filter(p=>p.iqStatus==="mismatch").length > 0 &&
                <span className="qn-badge" style={{borderColor:"var(--red)",color:"var(--red)"}}>
                  {elimPairs.filter(p=>p.iqStatus==="mismatch").length}
                </span>}
            </button>
            <button className={`qn-btn ${rightPane==="templates"?"on":""}`}
              onClick={()=>{setRightPane("templates");setActiveQueueItem(null)}}>
              <span style={{width:14,textAlign:"center",fontSize:12}}>⊟</span> Templates
              {templates.length>0 && <span className="qn-badge">{templates.length}</span>}
            </button>
          </div>

          <div className="qp-add">
            <button className="btn btn-acc btn-sm btn-block" onClick={startManualAllocation}>
              + New Allocation
            </button>
          </div>
        </div>

        {/* ════════ RIGHT MAIN PANE ════════ */}
        <main className="main-pane">
          <div className="mp-inner">

            {/* ── CONSOLIDATION READINESS ── */}
            {rightPane==="consolidation" && !activeQueueItem && (() => {
              const iqInBal   = elimPairs.filter(p=>p.iqStatus==="ok").length;
              const iqMis     = elimPairs.filter(p=>p.iqStatus==="mismatch").length;
              const iqNone    = elimPairs.filter(p=>p.iqStatus==="none").length;
              const hasImport = glImportRows.length > 0;
              const glInBal   = hasImport ? elimPairs.filter(p=>p.glStatus==="ok").length : null;
              const glMis     = hasImport ? elimPairs.filter(p=>p.glStatus==="mismatch").length : null;
              const hasDisc   = hasImport && elimPairs.some(p=>p.discStatus==="discrepancy");
              const allIqOk   = iqMis===0 && iqNone < elimPairs.length;
              const allGlOk   = hasImport && glMis===0;
              const canApprove= allIqOk && (!hasImport || allGlOk) && !hasDisc;

              // CSV parse helper
              function parseImportCsv(text) {
                const lines = text.trim().split(/\r?\n/);
                if (lines.length < 2) return { error:"File appears empty", rows:[] };
                const header = lines[0].split(",").map(h=>h.trim().toLowerCase());
                const idIdx  = header.findIndex(h=>h.includes("property")||h==="id"||h==="entity");
                const dtIdx  = header.findIndex(h=>h.includes("due_to")||h.includes("dueto")||h.includes("due to"));
                const dfIdx  = header.findIndex(h=>h.includes("due_from")||h.includes("duefrom")||h.includes("due from"));
                if (idIdx<0||dtIdx<0||dfIdx<0) return { error:"Expected columns: property_id, due_to, due_from", rows:[] };
                const rows = [];
                for (let i=1;i<lines.length;i++) {
                  const cols = lines[i].split(",");
                  if (cols.length<3) continue;
                  const propId  = cols[idIdx]?.trim();
                  const dueTo   = parseFloat(cols[dtIdx]?.replace(/[$,]/g,""))||0;
                  const dueFrom = parseFloat(cols[dfIdx]?.replace(/[$,]/g,""))||0;
                  if (propId) rows.push({propId,dueTo,dueFrom});
                }
                return { error:"", rows };
              }

              function downloadTemplate() {
                const header = "property_id,property_name,due_to_balance,due_from_balance";
                const rows = elimPairs.map(p=>`${p.id},"${p.name}",${p.iqDueTo.toFixed(2)},${p.iqDueFrom.toFixed(2)}`);
                const csv = [header,...rows].join("\n");
                const blob = new Blob([csv],{type:"text/csv"});
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement("a"); a.href=url; a.download="IC_GL_Import_Template.csv"; a.click();
              }

              function handleFileUpload(file) {
                if (!file) return;
                const reader = new FileReader();
                reader.onload = e => {
                  const text = e.target.result;
                  const {error,rows} = parseImportCsv(text);
                  if (error) { setImportError(error); setImportPreview([]); }
                  else { setImportError(""); setImportPreview(rows); }
                  setImportCsvText(text);
                };
                reader.readAsText(file);
              }

              return (
                <>
                  {/* Header */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                    <span style={{fontFamily:"var(--head)",fontSize:17,fontWeight:700,color:"var(--txb)"}}>
                      Consolidation Readiness
                    </span>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      {hasImport && (
                        <span className="import-badge">GL Imported — {glImportRows.length} entities</span>
                      )}
                      <button className="btn btn-sm" onClick={()=>setShowImportModal(true)}>
                        {hasImport ? "↺ Re-Import GL" : "⬆ Import GL Balances"}
                      </button>
                      {canApprove && !periodApproved && (
                        <button className="btn btn-acc btn-sm" onClick={async ()=>{
                          const { error } = await supabase.from("period_approvals").insert({ org_id: org.id, module: "intercoiq", period: CUR_PERIOD, approved_by: profile.id });
                          if (error) { msg(`Approve failed: ${error.message}`); return; }
                          setPeriodApproved(true);
                          setApprovedAt(new Date().toISOString());
                          msg("Period approved — IC eliminations cleared ✓");
                        }}>Approve Period →</button>
                      )}
                    </div>
                  </div>

                  {/* Period approved banner */}
                  {periodApproved && (
                    <div className="approve-banner">
                      <div>
                        <span style={{fontSize:11,fontWeight:700,color:"var(--green)"}}>✓ Period Approved</span>
                        <span style={{fontSize:9,color:"var(--mu)",marginLeft:10}}>Signed off {new Date(approvedAt).toLocaleString()} — IC eliminations cleared for consolidation</span>
                      </div>
                      <button className="btn btn-sm btn-ghost" onClick={async ()=>{await supabase.from("period_approvals").delete().eq("org_id", org.id).eq("module", "intercoiq").eq("period", CUR_PERIOD); setPeriodApproved(false); setApprovedAt(null);}}>Unlock</button>
                    </div>
                  )}

                  {/* Stats strip */}
                  <div className="stats" style={{marginBottom:20}}>
                    <div className="stat">
                      <div className="stat-val" style={{color: iqMis>0 ? "var(--red)" : "var(--green)"}}>
                        {iqMis>0 ? iqMis : iqInBal}
                      </div>
                      <div className="stat-lbl">{iqMis>0 ? "IQ Mismatches" : "IQ In Balance"}</div>
                    </div>
                    <div className="stat">
                      <div className="stat-val">{fmt(elimPairs.reduce((a,p)=>a+p.iqDueTo,0))}</div>
                      <div className="stat-lbl">Total IC Exposure</div>
                    </div>
                    <div className="stat">
                      <div className="stat-val" style={{color: hasImport ? (glMis>0 ? "var(--red)":"var(--green)") : "var(--di)"}}>
                        {hasImport ? (glMis>0 ? glMis : "✓ Clean") : "—"}
                      </div>
                      <div className="stat-lbl">GL Pairs {hasImport?"In Balance":"(No Import)"}</div>
                    </div>
                    <div className="stat">
                      <div className="stat-val" style={{color: hasImport ? (hasDisc?"var(--red)":"var(--green)") : "var(--di)"}}>
                        {hasImport ? (hasDisc ? "Discrepancies" : "✓ Clean") : "—"}
                      </div>
                      <div className="stat-lbl">IQ vs. GL Variance</div>
                    </div>
                  </div>

                  {/* Guidance banner */}
                  {iqMis > 0 && !periodApproved && (
                    <div style={{background:"var(--redBg)",border:"1px solid rgba(212,84,84,.3)",padding:"10px 14px",marginBottom:14,fontSize:10,color:"var(--red)",lineHeight:1.6}}>
                      <strong>⚠ {iqMis} pair{iqMis!==1?"s":""} out of balance.</strong> One-sided JE postings detected — the entity-side Due-To does not match the management-side Due-From. Navigate to <strong>Journal Log</strong>, find the affected JEs, reverse and repost with Post Mode = "Both Sides."
                    </div>
                  )}
                  {hasImport && hasDisc && (
                    <div style={{background:"var(--goldBg)",border:"1px solid var(--goldBd)",padding:"10px 14px",marginBottom:14,fontSize:10,color:"var(--gold)",lineHeight:1.6}}>
                      <strong>⚠ GL vs. IntercoIQ discrepancies detected.</strong> Your imported GL balances differ from IntercoIQ's posted amounts. This may indicate manual journal entries outside of IntercoIQ, unposted adjustments, or timing differences. Review the Elim Check tab for detail.
                    </div>
                  )}
                  {canApprove && !periodApproved && (
                    <div style={{background:"var(--greenBg)",border:"1px solid rgba(76,175,130,.3)",padding:"10px 14px",marginBottom:14,fontSize:10,color:"var(--green)",lineHeight:1.6}}>
                      <strong>✓ All pairs in balance.</strong> {hasImport ? "IntercoIQ and GL balances agree." : "No GL import provided — IQ-only check."} Ready to approve period and run consolidation eliminations.
                    </div>
                  )}

                  {/* Sub-tabs */}
                  <div className="elim-tabs">
                    <button className={`elim-tab ${consolidationTab==="matrix"?"on":""}`} onClick={()=>setConsolidationTab("matrix")}>IC Matrix</button>
                    <button className={`elim-tab ${consolidationTab==="elim"?"on":""}`} onClick={()=>setConsolidationTab("elim")}>Elim Check</button>
                  </div>

                  {/* ── IC MATRIX TAB ── */}
                  {consolidationTab==="matrix" && (() => {
                    const entities = [...elimPairs.map(p=>({id:p.id,name:p.name})), {id:"MGMT",name:"Mgmt Co"}];
                    // Build NxN matrix values
                    // matrix[rowId][colId] = net balance row entity sees vs col entity
                    const matrixVal = (rowId, colId) => {
                      if (rowId===colId) return null; // diagonal
                      if (colId==="MGMT") {
                        const p = elimPairs.find(x=>x.id===rowId);
                        return p ? -(p.iqDueTo) : 0; // property owes MGMT (negative = liability)
                      }
                      if (rowId==="MGMT") {
                        const p = elimPairs.find(x=>x.id===colId);
                        return p ? p.iqDueFrom : 0; // MGMT is owed by property (positive = asset)
                      }
                      return 0; // property-to-property (no cross-property JEs in current data)
                    };
                    const allPropZero = elimPairs.every(p=>p.iqDueTo===0&&p.iqDueFrom===0);
                    const colCount = entities.length + 2; // label col + entity cols
                    return (
                      <>
                        <div style={{fontSize:9,color:"var(--mu)",marginBottom:12,lineHeight:1.6}}>
                          Net bilateral balance between each entity pair. Positive = row entity is <span style={{color:"var(--acc)"}}>owed money</span> by column entity. Negative = row entity <span style={{color:"var(--red)"}}>owes money</span> to column entity. Each pair should sum to zero for elimination.
                        </div>
                        {allPropZero && (
                          <div style={{padding:"24px",textAlign:"center",border:"1px dashed var(--b2)",fontSize:10,color:"var(--mu)",marginBottom:16}}>
                            No posted JEs — IC balances will appear here as allocations are posted.
                          </div>
                        )}
                        {!allPropZero && (
                          <div style={{overflowX:"auto",marginBottom:16}}>
                            <table className="elim-tbl">
                              <thead>
                                <tr>
                                  <th style={{minWidth:160}}>Entity</th>
                                  {entities.map(e=>(
                                    <th key={e.id} className="r" style={{minWidth:90,fontSize:7,color:e.id==="MGMT"?"var(--gold)":"var(--mu)"}}>
                                      {e.id==="MGMT"?"Mgmt Co":e.id}
                                    </th>
                                  ))}
                                  <th className="r">Net Position</th>
                                </tr>
                              </thead>
                              <tbody>
                                {entities.map(rowE => {
                                  const netPos = entities.reduce((sum,colE)=>{
                                    if (colE.id===rowE.id) return sum;
                                    const v = matrixVal(rowE.id,colE.id);
                                    return sum + (v||0);
                                  },0);
                                  return (
                                    <tr key={rowE.id} style={{background:rowE.id==="MGMT"?"var(--s2)":""}}>
                                      <td style={{fontWeight:600,fontSize:10,color:rowE.id==="MGMT"?"var(--gold)":"var(--tx)",borderRight:"1px solid var(--b2)"}}>
                                        {rowE.id==="MGMT"?"Berger Mgmt Co":rowE.name}
                                        <div style={{fontSize:8,color:"var(--mu)",fontWeight:400}}>{rowE.id}</div>
                                      </td>
                                      {entities.map(colE => {
                                        if (colE.id===rowE.id) return (
                                          <td key={colE.id} className="r mc-diag" style={{background:"var(--s2)",borderRight:"1px solid var(--b1)"}}>—</td>
                                        );
                                        const v = matrixVal(rowE.id,colE.id);
                                        const isZero = !v || Math.abs(v)<0.01;
                                        // Check if this pair is in balance (v + mirror = 0)
                                        const mirror = matrixVal(colE.id,rowE.id);
                                        const pairOk = Math.abs((v||0)+(mirror||0))<0.01;
                                        return (
                                          <td key={colE.id} className="r" style={{
                                            borderRight:"1px solid var(--b1)",
                                            background: isZero ? "" : pairOk ? "rgba(76,175,130,.05)" : "rgba(212,84,84,.07)",
                                          }}>
                                            {isZero ? <span style={{color:"var(--di)"}}>—</span> :
                                              <span style={{color: v>0 ? "var(--acc)" : "var(--red)",fontWeight:600}}>
                                                {v>0?"+":""}{fmtShort(v)}
                                              </span>
                                            }
                                          </td>
                                        );
                                      })}
                                      <td className="r" style={{fontWeight:700,borderLeft:"1px solid var(--b2)"}}>
                                        {Math.abs(netPos)<0.01 && (elimPairs.find(p=>p.id===rowE.id)?.iqDueTo||rowE.id==="MGMT")
                                          ? <span style={{color:"var(--green)"}}>✓ Flat</span>
                                          : Math.abs(netPos)<0.01 ? <span style={{color:"var(--di)"}}>—</span>
                                          : <span style={{color:netPos>0?"var(--acc)":"var(--red)"}}>{netPos>0?"+":""}{fmtShort(netPos)}</span>
                                        }
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <div style={{fontSize:9,color:"var(--mu)",lineHeight:1.6,padding:"10px 12px",background:"var(--s1)",border:"1px solid var(--b1)"}}>
                          <strong style={{color:"var(--tx)"}}>Note:</strong> This matrix reflects IC balances from IntercoIQ-posted journal entries only. For a GL-level check against your ERP's actual balances, use <strong>Import GL Balances</strong> above and review the Elim Check tab.
                          {elimPairs.every(p=>p.iqDueTo===0&&p.iqDueFrom===0) ? "" : " Entity-to-entity allocations will also appear here once posted."}
                        </div>
                      </>
                    );
                  })()}

                  {/* ── ELIM CHECK TAB ── */}
                  {consolidationTab==="elim" && (
                    <>
                      <div style={{fontSize:9,color:"var(--mu)",marginBottom:12,lineHeight:1.6}}>
                        Each row is one bilateral pair. For elimination to work, each pair's Due-To and Due-From must match exactly.
                        {hasImport ? " GL balances imported — comparing IQ vs. ERP." : " No GL import — showing IntercoIQ-only check."}
                      </div>
                      <table className="elim-tbl">
                        <thead>
                          <tr>
                            <th style={{minWidth:140}}>Entity</th>
                            <th>Entity B</th>
                            <th className="r">IQ Due-To (Prop)</th>
                            <th className="r">IQ Due-From (Mgmt)</th>
                            <th className="r">IQ Difference</th>
                            {hasImport && <th className="r" style={{color:"var(--gold)"}}>GL Due-To</th>}
                            {hasImport && <th className="r" style={{color:"var(--gold)"}}>GL Due-From</th>}
                            {hasImport && <th className="r" style={{color:"var(--gold)"}}>GL Difference</th>}
                            {hasImport && <th className="r" style={{color:"var(--gold)"}}>IQ vs. GL</th>}
                            <th>IQ Status</th>
                            {hasImport && <th>GL Status</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {elimPairs.map(p => {
                            const isBad = p.iqStatus==="mismatch";
                            const isGlBad = hasImport && p.glStatus==="mismatch";
                            const isDisc = hasImport && p.discStatus==="discrepancy";
                            return (
                              <tr key={p.id} className={isBad||isGlBad||isDisc?"bad-row":""}>
                                <td style={{fontWeight:600}}>
                                  {p.name}
                                  <div style={{fontSize:8,color:"var(--mu)",fontWeight:400}}>{p.id}</div>
                                </td>
                                <td style={{color:"var(--gold)",fontSize:10}}>Mgmt Co</td>
                                {/* IQ columns */}
                                <td className="r" style={{color: p.iqDueTo>0 ? "var(--green)" : "var(--di)"}}>{p.iqDueTo>0 ? fmt(p.iqDueTo) : "—"}</td>
                                <td className="r" style={{color: p.iqDueFrom>0 ? "var(--acc)" : "var(--di)"}}>{p.iqDueFrom>0 ? fmt(p.iqDueFrom) : "—"}</td>
                                <td className="r" style={{fontWeight:700,color: Math.abs(p.iqDiff)<0.01&&p.iqDueTo>0 ? "var(--green)" : p.iqStatus==="mismatch" ? "var(--red)" : "var(--di)"}}>
                                  {p.iqStatus==="none" ? "—" : Math.abs(p.iqDiff)<0.01 ? "✓" : fmt(p.iqDiff)}
                                </td>
                                {/* GL columns */}
                                {hasImport && <td className="r" style={{color:p.glDueTo!=null&&p.glDueTo>0?"var(--green)":"var(--di)"}}>{p.glDueTo!=null&&p.glDueTo>0?fmt(p.glDueTo):"—"}</td>}
                                {hasImport && <td className="r" style={{color:p.glDueFrom!=null&&p.glDueFrom>0?"var(--acc)":"var(--di)"}}>{p.glDueFrom!=null&&p.glDueFrom>0?fmt(p.glDueFrom):"—"}</td>}
                                {hasImport && <td className="r" style={{fontWeight:700,color:p.glDiff===null?"var(--di)":Math.abs(p.glDiff)<0.01?"var(--green)":"var(--red)"}}>
                                  {p.glDiff===null?"—":Math.abs(p.glDiff)<0.01?"✓":fmt(p.glDiff)}
                                </td>}
                                {hasImport && <td className="r" style={{fontWeight:700,color:p.discStatus==="discrepancy"?"var(--gold)":"var(--green)"}}>
                                  {p.glDueTo===null ? <span style={{color:"var(--di)"}}>No data</span>
                                    : p.discStatus==="discrepancy"
                                    ? <span style={{color:"var(--gold)"}}>{fmt(Math.abs(p.glIqDueToDisc||0)+Math.abs(p.glIqDueFromDisc||0))}</span>
                                    : <span style={{color:"var(--green)"}}>✓ Match</span>}
                                </td>}
                                {/* Status badges */}
                                <td>
                                  <span className={`elim-status ${p.iqStatus==="ok"?"es-ok":p.iqStatus==="mismatch"?"es-bad":"es-none"}`}>
                                    {p.iqStatus==="ok"?"In Balance":p.iqStatus==="mismatch"?"Out of Balance":"No Activity"}
                                  </span>
                                </td>
                                {hasImport && <td>
                                  <span className={`elim-status ${p.glStatus==="ok"?"es-ok":p.glStatus==="mismatch"?"es-bad":p.glStatus==="no_import"?"es-none":"es-warn"}`}>
                                    {p.glStatus==="ok"?"In Balance":p.glStatus==="mismatch"?"Out of Balance":p.glDueTo===null?"No Data":"—"}
                                  </span>
                                </td>}
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} style={{fontWeight:700,fontSize:10,padding:"8px 10px",background:"var(--s2)",borderTop:"2px solid var(--b2)"}}>TOTALS</td>
                            <td className="r" style={{fontWeight:700,background:"var(--s2)",borderTop:"2px solid var(--b2)",color:"var(--green)"}}>{fmt(elimPairs.reduce((a,p)=>a+p.iqDueTo,0))}</td>
                            <td className="r" style={{fontWeight:700,background:"var(--s2)",borderTop:"2px solid var(--b2)",color:"var(--acc)"}}>{fmt(elimPairs.reduce((a,p)=>a+p.iqDueFrom,0))}</td>
                            <td className="r" style={{fontWeight:700,background:"var(--s2)",borderTop:"2px solid var(--b2)",color:Math.abs(elimPairs.reduce((a,p)=>a+p.iqDiff,0))<0.01?"var(--green)":"var(--red)"}}>
                              {Math.abs(elimPairs.reduce((a,p)=>a+p.iqDiff,0))<0.01?"✓ Balanced":fmt(elimPairs.reduce((a,p)=>a+p.iqDiff,0))}
                            </td>
                            {hasImport && <td className="r" style={{fontWeight:700,background:"var(--s2)",borderTop:"2px solid var(--b2)",color:"var(--green)"}}>{fmt(elimPairs.reduce((a,p)=>a+(p.glDueTo||0),0))}</td>}
                            {hasImport && <td className="r" style={{fontWeight:700,background:"var(--s2)",borderTop:"2px solid var(--b2)",color:"var(--acc)"}}>{fmt(elimPairs.reduce((a,p)=>a+(p.glDueFrom||0),0))}</td>}
                            {hasImport && <td colSpan={2} style={{background:"var(--s2)",borderTop:"2px solid var(--b2)"}} />}
                            <td style={{background:"var(--s2)",borderTop:"2px solid var(--b2)"}} />
                            {hasImport && <td style={{background:"var(--s2)",borderTop:"2px solid var(--b2)"}} />}
                          </tr>
                        </tfoot>
                      </table>
                    </>
                  )}
                </>
              );
            })()}

            {/* ── DASHBOARD — PROPERTY RECONCILIATION ── */}
            {rightPane==="dashboard" && !activeQueueItem && (
              <>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <span style={{fontFamily:"var(--head)",fontSize:17,fontWeight:700,color:"var(--txb)"}}>
                    Entity Reconciliation
                  </span>
                  <button className="btn btn-acc btn-sm" onClick={startManualAllocation}>+ New Allocation</button>
                </div>

                {/* Summary stats */}
                <div className="stats">
                  <div className="stat"><div className="stat-val">{fmt(totalExpense)}</div><div className="stat-lbl">Expense Allocated</div></div>
                  <div className="stat"><div className="stat-val" style={{color:"var(--green)"}}>{fmt(totalDueTo)}</div><div className="stat-lbl">IC Due To (Cr)</div></div>
                  <div className="stat"><div className="stat-val">{fmt(totalDueFrom)}</div><div className="stat-lbl">IC Due From (Dr)</div></div>
                  <div className="stat">
                    <div className="stat-val" style={{color:Math.abs(totalVariance) < 0.01 ? "var(--green)" : "var(--red)"}}>
                      {Math.abs(totalVariance) < 0.01 ? "✓ Balanced" : fmt(totalVariance)}
                    </div>
                    <div className="stat-lbl">IC Variance</div>
                  </div>
                </div>

                {/* Mismatch alert */}
                {mismatchCount > 0 && (
                  <div style={{background:"var(--redBg)",border:"1px solid rgba(212,84,84,.3)",padding:"10px 14px",marginBottom:14,fontSize:10,color:"var(--red)",lineHeight:1.6}}>
                    <div style={{fontWeight:700,marginBottom:3}}>⚠ {mismatchCount} {mismatchCount===1?"entity has":"entities have"} IC balance mismatches</div>
                    <div style={{fontSize:9}}>One-sided JE postings detected — the entity-side Due-To does not match the management-side Due-From. Review flagged entities below.</div>
                  </div>
                )}

                {/* Pending queue alert */}
                {pendingTxns.length > 0 && (
                  <div style={{background:"var(--goldBg)",border:"1px solid var(--goldBd)",padding:"10px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:10,color:"var(--gold)",marginBottom:3}}>{pendingTxns.length} unallocated {pendingTxns.length===1?"transaction":"transactions"} in queue</div>
                      <div style={{fontSize:9,color:"var(--gold)"}}>{fmt(pendingTxns.reduce((a,t)=>a+t.amount,0))} total · from GL watchlist</div>
                    </div>
                    <button className="btn btn-gold btn-sm" onClick={()=>loadTxn(pendingTxns[0])}>Process Next →</button>
                  </div>
                )}

                {/* Property recon grid */}
                <div style={{marginBottom:0}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:9,letterSpacing:".14em",textTransform:"uppercase",color:"var(--acc)",fontWeight:600}}>By Entity</span>
                      <span className="badge b-dim">{properties.length} properties</span>
                      {mismatchCount > 0 && <span className="badge b-red">{mismatchCount} mismatch{mismatchCount!==1?"es":""}</span>}
                    </div>
                    <input
                      className="pr-search"
                      placeholder="Filter entities…"
                      value={propFilter}
                      onChange={(e) => setPropFilter(e.target.value)}
                    />
                  </div>

                  {/* Column headers */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 110px 110px 90px 110px 50px",padding:"6px 16px",gap:8,borderBottom:"1px solid var(--b2)"}}>
                    <span style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--mu)"}}>Entity</span>
                    <span style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--mu)",textAlign:"right"}}>IC Due To (Cr)</span>
                    <span style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--mu)",textAlign:"right"}}>IC Due From (Dr)</span>
                    <span style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--mu)",textAlign:"right"}}>Variance</span>
                    <span style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--mu)",textAlign:"right"}}>Expense</span>
                    <span style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--mu)",textAlign:"right"}}>JEs</span>
                  </div>

                  {/* Property rows */}
                  {filteredRecon.map((prop) => {
                    const isExpanded = expandedProp === prop.id;
                    const glEntries = Object.entries(prop.glBreakdown);
                    return (
                      <div key={prop.id} className={`pr-row ${isExpanded ? "expanded" : ""}`}>
                        <div className="pr-header" onClick={() => setExpandedProp(isExpanded ? null : prop.id)}>
                          <div className="pr-name">
                            <span className="pr-arrow">▸</span>
                            {prop.name}
                            {prop.hasMismatch && <span style={{fontSize:9,color:"var(--red)",fontWeight:700,marginLeft:2}}>⚠</span>}
                            <span style={{fontSize:8,color:"var(--mu)",fontWeight:400}}>{prop.units}u</span>
                          </div>
                          <div className="pr-val" style={{color:prop.icDueTo > 0 ? "var(--green)" : "var(--di)"}}>{fmt(prop.icDueTo)}</div>
                          <div className="pr-val" style={{color:prop.icDueFrom > 0 ? "var(--acc)" : "var(--di)"}}>{fmt(prop.icDueFrom)}</div>
                          <div className="pr-val" style={{color:prop.hasMismatch ? "var(--red)" : Math.abs(prop.variance) < 0.01 && prop.icDueTo > 0 ? "var(--green)" : "var(--di)",fontWeight:prop.hasMismatch ? 700 : 600}}>
                            {prop.icDueTo === 0 && prop.icDueFrom === 0 ? "—" : prop.hasMismatch ? fmt(prop.variance) : "✓"}
                          </div>
                          <div className="pr-val" style={{color:prop.expenseTotal > 0 ? "var(--txb)" : "var(--di)"}}>{fmt(prop.expenseTotal)}</div>
                          <div className="pr-pct">{prop.jeCount || "—"}</div>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="pr-detail">
                            {prop.jeCount === 0 ? (
                              <div style={{padding:"16px",textAlign:"center",fontSize:9,color:"var(--mu)"}}>No allocations to this entity yet</div>
                            ) : (
                              <>
                                {/* Mismatch explanation if applicable */}
                                {prop.hasMismatch && (
                                  <div style={{padding:"8px 16px",background:"var(--redBg)",borderBottom:"1px solid rgba(212,84,84,.2)",fontSize:9,color:"var(--red)",lineHeight:1.5}}>
                                    <strong>IC Mismatch: </strong>
                                    Due To (Cr) of {fmt(prop.icDueTo)} ≠ Due From (Dr) of {fmt(prop.icDueFrom)}.
                                    {" "}Variance of <strong>{fmt(prop.variance)}</strong> caused by {prop.mismatchJEs.length} one-sided {prop.mismatchJEs.length===1?"posting":"postings"}:
                                    {" "}{prop.mismatchJEs.map(j=>`${j.id} (${j.postMode==="property_only"?"property only":"mgmt only"})`).join(", ")}
                                  </div>
                                )}

                                {/* GL Breakdown */}
                                {glEntries.length > 0 && (
                                  <div style={{padding:"10px 16px",borderBottom:"1px solid var(--b1)"}}>
                                    <div style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--acc)",fontWeight:600,marginBottom:6}}>GL Account Breakdown</div>
                                    <div style={{display:"grid",gridTemplateColumns:"1fr 100px 100px 100px",gap:4}}>
                                      <span style={{fontSize:7,color:"var(--mu)",letterSpacing:".1em",textTransform:"uppercase"}}>GL Account</span>
                                      <span style={{fontSize:7,color:"var(--mu)",letterSpacing:".1em",textTransform:"uppercase",textAlign:"right"}}>Expense (Dr)</span>
                                      <span style={{fontSize:7,color:"var(--mu)",letterSpacing:".1em",textTransform:"uppercase",textAlign:"right"}}>Due To (Cr)</span>
                                      <span style={{fontSize:7,color:"var(--mu)",letterSpacing:".1em",textTransform:"uppercase",textAlign:"right"}}>Due From (Dr)</span>
                                    </div>
                                    {glEntries.map(([code, gl]) => (
                                      <div key={code} style={{display:"grid",gridTemplateColumns:"1fr 100px 100px 100px",gap:4,padding:"3px 0",borderTop:"1px solid var(--b1)"}}>
                                        <span style={{fontSize:10,fontWeight:500}}>{code} {gl.name}</span>
                                        <span style={{fontSize:10,textAlign:"right",color:"var(--acc)"}}>{fmt(gl.expense)}</span>
                                        <span style={{fontSize:10,textAlign:"right",color:"var(--green)"}}>{fmt(gl.dueTo)}</span>
                                        <span style={{fontSize:10,textAlign:"right",color:Math.abs(gl.dueTo - gl.dueFrom) >= 0.01 ? "var(--red)" : "var(--acc)"}}>{fmt(gl.dueFrom)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Individual JEs */}
                                <table className="tbl">
                                  <thead>
                                    <tr>
                                      <th>JE ID</th>
                                      <th>Vendor</th>
                                      <th>Expense GL</th>
                                      <th>Post Mode</th>
                                      <th>Method</th>
                                      <th style={{textAlign:"right"}}>Amount</th>
                                      <th style={{textAlign:"right"}}>%</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {prop.jes.map(je => {
                                      const propLine = je.lines.find(l => l.property.id === prop.id);
                                      const isOneSided = je.postMode === "property_only" || je.postMode === "mgmt_only";
                                      return (
                                        <tr key={je.id} style={isOneSided ? {background:"var(--redBg)"} : {}}>
                                          <td style={{color:"var(--acc)",fontWeight:600}}>{je.id}</td>
                                          <td style={{fontWeight:500}}>{je.vendor}</td>
                                          <td style={{color:"var(--mu)"}}>{je.expenseGl?.code} {je.expenseGl?.name}</td>
                                          <td>
                                            <span className={`badge ${isOneSided ? "b-red" : "b-green"}`}>
                                              {je.postMode==="both"?"Full JE":je.postMode==="property_only"?"Prop Only":"Mgmt Only"}
                                            </span>
                                          </td>
                                          <td style={{color:"var(--mu)"}}>{je.method?.name}</td>
                                          <td style={{textAlign:"right",fontWeight:600,color:"var(--acc)"}}>{fmt(propLine?.amount || 0)}</td>
                                          <td style={{textAlign:"right",color:"var(--mu)"}}>{propLine?.pct?.toFixed(1)}%</td>
                                          <td><span className={`badge ${je.status==="posted"?"b-green":"b-dim"}`}>{je.status}</span></td>
                                        </tr>
                                      );
                                    })}
                                    {/* Property subtotal */}
                                    <tr style={{background:"var(--s2)"}}>
                                      <td colSpan={5} style={{fontWeight:700,fontSize:9,color:"var(--acc)",letterSpacing:".06em",textTransform:"uppercase"}}>
                                        {prop.name} Total
                                        {prop.hasMismatch && <span style={{color:"var(--red)",marginLeft:8}}>⚠ IC Mismatch</span>}
                                      </td>
                                      <td style={{textAlign:"right",fontWeight:700,color:"var(--acc)"}}>{fmt(prop.expenseTotal)}</td>
                                      <td></td>
                                      <td></td>
                                    </tr>
                                  </tbody>
                                </table>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {filteredRecon.length === 0 && (
                    <div className="empty" style={{padding:"24px"}}>
                      <div style={{fontSize:10}}>No entities match filter</div>
                    </div>
                  )}

                  {/* Grand total row */}
                  {filteredRecon.length > 0 && (
                    <div className="pr-totals">
                      <div style={{fontSize:9,color:"var(--acc)",letterSpacing:".06em",textTransform:"uppercase"}}>
                        Grand Total — {properties.length} entities
                        {mismatchCount > 0 && <span style={{color:"var(--red)",marginLeft:8}}>⚠ {mismatchCount} mismatch{mismatchCount!==1?"es":""}</span>}
                      </div>
                      <div style={{textAlign:"right",color:"var(--green)",fontSize:11}}>{fmt(totalDueTo)}</div>
                      <div style={{textAlign:"right",color:"var(--acc)",fontSize:11}}>{fmt(totalDueFrom)}</div>
                      <div style={{textAlign:"right",color:Math.abs(totalVariance) < 0.01 ? "var(--green)" : "var(--red)",fontSize:11}}>
                        {Math.abs(totalVariance) < 0.01 ? "✓" : fmt(totalVariance)}
                      </div>
                      <div style={{textAlign:"right",color:"var(--txb)",fontSize:11}}>{fmt(totalExpense)}</div>
                      <div style={{textAlign:"right",color:"var(--mu)",fontSize:9}}>{totalJECount}</div>
                    </div>
                  )}
                </div>

                {/* Quick actions row below grid */}
                <div className="g2" style={{marginTop:16}}>
                  <div className="card" style={{marginBottom:0}}>
                    <div className="card-hd"><span className="card-title">Connected Systems</span></div>
                    <div className="card-body">
                      {CONNECTORS.filter(c=>connectedSystems.has(c.id)).map(c=>(
                        <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"1px solid var(--b1)"}}>
                          <div style={{width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",background:c.logoColor+"18",border:`1px solid ${c.logoColor}40`,color:c.logoColor,fontFamily:"var(--head)",fontWeight:900,fontSize:9}}>{c.logo}</div>
                          <span style={{fontSize:11,fontWeight:600,flex:1}}>{c.name}</span>
                          <span className="badge b-green">Live</span>
                        </div>
                      ))}
                      {connectedSystems.size===0 && <div style={{fontSize:10,color:"var(--mu)"}}>No systems connected. <span style={{color:"var(--acc)",cursor:"pointer"}} onClick={()=>{setDrawerOpen(true);setDrawerTab("connectors")}}>Set up →</span></div>}
                    </div>
                  </div>
                  <div className="card" style={{marginBottom:0}}>
                    <div className="card-hd"><span className="card-title">Activity</span></div>
                    <div className="card-body" style={{display:"flex",flexDirection:"column",gap:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,padding:"4px 0",borderBottom:"1px solid var(--b1)"}}>
                        <span style={{color:"var(--mu)"}}>Posted JEs</span><span style={{fontWeight:600}}>{totalJECount}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,padding:"4px 0",borderBottom:"1px solid var(--b1)"}}>
                        <span style={{color:"var(--mu)"}}>Entities w/ Allocations</span><span style={{fontWeight:600}}>{propertiesWithAllocations}/{properties.length}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,padding:"4px 0",borderBottom:"1px solid var(--b1)"}}>
                        <span style={{color:"var(--mu)"}}>Pending Queue</span><span style={{fontWeight:600,color:pendingTxns.length > 0 ? "var(--gold)" : "var(--mu)"}}>{pendingTxns.length}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,padding:"4px 0"}}>
                        <span style={{color:"var(--mu)"}}>IC Mismatches</span><span style={{fontWeight:600,color:mismatchCount > 0 ? "var(--red)" : "var(--green)"}}>{mismatchCount > 0 ? mismatchCount : "✓ None"}</span>
                      </div>
                      <button className="btn btn-ghost btn-block btn-sm" style={{marginTop:4}} onClick={()=>{setRightPane("journal");setActiveQueueItem(null)}}>View Journal Log →</button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── ALLOCATION WORKSPACE ── */}
            {(rightPane==="allocate" || activeQueueItem) && (
              <>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontFamily:"var(--head)",fontSize:15,fontWeight:700,color:"var(--txb)"}}>
                      {inv.sourceTxnId ? `Allocate — ${inv.vendor || "Transaction"}` : "New Allocation"}
                    </span>
                    {inv.sourceTxnId && <span className="badge b-acc">GL Pull</span>}
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button className="btn btn-ghost btn-sm" onClick={saveAsTemplate}>Save as Template</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setInv(blank);setManualPcts({});setPropSearch("");setActiveQueueItem(null);setRightPane("dashboard")}}>Cancel</button>
                  </div>
                </div>

                {/* Section 1: Invoice Details */}
                <div className="card">
                  <div className="card-body">
                    <div className="sec-hd"><div className="sec-num">1</div><span className="sec-label">Invoice Details</span><div className="sec-line"/></div>
                    <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:10}}>
                      <div className="field"><label className="lbl">Vendor *</label><input className="inp" value={inv.vendor} onChange={e=>sf("vendor",e.target.value)} placeholder="Vendor name"/></div>
                      <div className="field"><label className="lbl">Invoice #</label><input className="inp" value={inv.invoiceNo} onChange={e=>sf("invoiceNo",e.target.value)} placeholder="INV-0001"/></div>
                      <div className="field"><label className="lbl">Date</label><input type="date" className="inp" value={inv.date} onChange={e=>sf("date",e.target.value)}/></div>
                      <div className="field"><label className="lbl">Amount *</label><input className="inp" type="number" min="0" step="0.01" value={inv.amount} onChange={e=>sf("amount",e.target.value)} placeholder="0.00"/></div>
                    </div>
                    <div className="g2">
                      <div className="field" style={{marginBottom:0}}><label className="lbl">Description</label><input className="inp" value={inv.description} onChange={e=>sf("description",e.target.value)} placeholder="e.g. Q1 Software License"/></div>
                      <div className="field" style={{marginBottom:0}}><label className="lbl">Memo (each JE line)</label><input className="inp" value={inv.memo} onChange={e=>sf("memo",e.target.value)} placeholder="Optional"/></div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Properties + Method side-by-side */}
                <div className="ws-grid">
                  <div className="card">
                    <div className="card-body">
                      <div className="sec-hd"><div className="sec-num">2</div><span className="sec-label">Properties</span><div className="sec-line"/>
                        <button className="btn btn-xs btn-ghost" onClick={()=>setInv(p=>({...p,selectedProps:filteredProps.map(x=>x.id)}))}>All</button>
                        <button className="btn btn-xs btn-ghost" onClick={()=>setInv(p=>({...p,selectedProps:[]}))}>None</button>
                      </div>
                      <div className="pp-groups">
                        {groups.map(g=>(
                          <span key={g.id} className={`pp-gtag ${inv.activeGroup===g.id?"on":""}`}
                            onClick={()=>selectGroup(g.id)}>{g.name}</span>
                        ))}
                      </div>
                      <div className="pp-search">
                        <input className="inp inp-sm" value={propSearch} onChange={e=>setPropSearch(e.target.value)} placeholder="Search properties…"/>
                      </div>
                      <div className="pp-list">
                        {filteredProps.map(p=>{
                          const sel = inv.selectedProps.includes(p.id);
                          return (
                            <div key={p.id} className={`pp-row ${sel?"sel":""}`} onClick={()=>toggleProp(p.id)}>
                              <div className="pp-check">{sel?"✓":""}</div>
                              <span className="pp-name">{p.name}</span>
                              <span className="pp-detail">{p.units}u</span>
                              <span className="pp-detail">{p.employees}e</span>
                              <span className="pp-detail" style={{color:"var(--di)",fontSize:8}}>{p.region}</span>
                            </div>
                          );
                        })}
                      </div>
                      {inv.selectedProps.length>0 && (
                        <div style={{marginTop:6,fontSize:9,color:"var(--mu)"}}>
                          {inv.selectedProps.length} selected · {selProps.reduce((a,p)=>a+p.units,0)} total units
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    {/* Method */}
                    <div className="card">
                      <div className="card-body">
                        <div className="sec-hd"><div className="sec-num">3</div><span className="sec-label">Allocation Method</span><div className="sec-line"/></div>
                        <div className="ms-grid">
                          {methods.map(m=>(
                            <div key={m.id} className={`ms-card ${inv.methodId===m.id?"on":""}`} onClick={()=>sf("methodId",m.id)}>
                              <span className="ms-tag">{m.custom?"Custom":m.driver}</span>
                              <div className="ms-name">{m.name}</div>
                              <div className="ms-desc">{m.description}</div>
                            </div>
                          ))}
                        </div>
                        {selMethod?.driver==="manual" && inv.selectedProps.length>0 && (
                          <div style={{marginTop:10}}>
                            {inv.selectedProps.map(pid=>{
                              const p=properties.find(x=>x.id===pid);
                              return (
                                <div key={pid} className="mp-row">
                                  <span style={{flex:1,fontSize:10}}>{p?.name}</span>
                                  <input className="mp-inp" type="number" min="0" max="100" step="0.01"
                                    value={manualPcts[pid]||""}
                                    onChange={e=>setManualPcts(prev=>({...prev,[pid]:e.target.value}))}
                                    placeholder="0"/>
                                  <span style={{fontSize:9,color:"var(--mu)"}}>%</span>
                                </div>
                              );
                            })}
                            <div style={{fontSize:10,marginTop:6,color:Math.abs(mPctTotal-100)<0.01?"var(--green)":"var(--red)"}}>
                              {mPctTotal.toFixed(1)}% {Math.abs(mPctTotal-100)<0.01?"✓":`(${(100-mPctTotal).toFixed(1)}% remaining)`}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* GL & Post Mode */}
                    <div className="card">
                      <div className="card-body">
                        <div className="sec-hd"><div className="sec-num">4</div><span className="sec-label">GL & Posting</span><div className="sec-line"/></div>
                        <div className="field"><label className="lbl">Expense GL *</label>
                          <select className="sel" value={inv.expenseGl} onChange={e=>sf("expenseGl",e.target.value)}>
                            <option value="">— Select —</option>
                            {glAccounts.filter(g=>g.entity==="expense").map(g=>(
                              <option key={g.code} value={g.code}>{g.code} – {g.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="lbl" style={{marginBottom:4}}>Post Mode</div>
                        <div className="pm-row">
                          {[{id:"both",label:"Full JE"},{id:"property_only",label:"Prop Side"},{id:"mgmt_only",label:"Mgmt Side"}].map(o=>(
                            <div key={o.id} className={`pm-opt ${inv.postMode===o.id?"on":""}`} onClick={()=>sf("postMode",o.id)}>{o.label}</div>
                          ))}
                        </div>
                        {(inv.postMode==="both"||inv.postMode==="property_only") && (
                          <div className="field"><label className="lbl">IC Credit GL (Due To) *</label>
                            <select className="sel" value={inv.icCreditGl} onChange={e=>sf("icCreditGl",e.target.value)}>
                              <option value="">— Select —</option>
                              {glAccounts.filter(g=>g.entity==="ic").map(g=>(
                                <option key={g.code} value={g.code}>{g.code} – {g.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {(inv.postMode==="both"||inv.postMode==="mgmt_only") && (
                          <div className="field" style={{marginBottom:0}}><label className="lbl">IC Debit GL (Due From) *</label>
                            <select className="sel" value={inv.icDebitGl} onChange={e=>sf("icDebitGl",e.target.value)}>
                              <option value="">— Select —</option>
                              {glAccounts.filter(g=>g.entity==="ic").map(g=>(
                                <option key={g.code} value={g.code}>{g.code} – {g.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* JE Preview — full width */}
                {allocLines.length>0 && inv.expenseGl && (inv.icCreditGl||inv.postMode==="mgmt_only") && (
                  <div className="card">
                    <div className="card-hd">
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span className="card-title">JE Preview</span>
                        <span className="badge b-acc">{allocLines.length} properties</span>
                        <span className="badge b-dim">{inv.postMode==="both"?"Full JE":inv.postMode==="property_only"?"Prop Side":"Mgmt Side"}</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span className={`bal ${isBalanced?"bal-ok":"bal-err"}`}>
                          {isBalanced ? "Balanced ✓" : "Out of Balance"}
                        </span>
                        <span style={{fontFamily:"var(--head)",fontWeight:700,fontSize:16,color:"var(--txb)"}}>{fmt(parseFloat(inv.amount))}</span>
                      </div>
                    </div>
                    <div className="scroll-x">
                      <table className="tbl">
                        <thead><tr><th>Entity</th><th>Type</th><th>GL Account</th><th>Memo</th><th style={{textAlign:"right"}}>Debit</th><th style={{textAlign:"right"}}>Credit</th><th style={{width:50}}>%</th></tr></thead>
                        <tbody>
                          {allocLines.map((line,i)=>(
                            <Fragment key={i}>
                              {(inv.postMode==="both"||inv.postMode==="property_only") && (
                                <tr>
                                  <td style={{fontWeight:600,fontSize:10}}>{line.property.name}</td>
                                  <td><span className="badge b-acc">Expense</span></td>
                                  <td style={{fontSize:9}}>{expGl?.code} {expGl?.name}</td>
                                  <td style={{fontSize:9,color:"var(--mu)"}}>{inv.memo||inv.description} [ICPIQ]</td>
                                  <td className="dr" style={{textAlign:"right",fontWeight:600}}>{fmt(line.amount)}</td>
                                  <td></td>
                                  <td style={{fontSize:9,color:"var(--mu)"}}>{line.pct.toFixed(1)}%</td>
                                </tr>
                              )}
                              {(inv.postMode==="both"||inv.postMode==="property_only") && (
                                <tr>
                                  <td className="indent">↳ {line.property.id}</td>
                                  <td><span className="badge b-gold">IC Cr</span></td>
                                  <td style={{fontSize:9}}>{icCrGl?.code} {icCrGl?.name}</td>
                                  <td style={{fontSize:9,color:"var(--mu)"}}>[ICPIQ]</td>
                                  <td></td>
                                  <td className="cr" style={{textAlign:"right",fontWeight:600}}>{fmt(line.amount)}</td>
                                  <td></td>
                                </tr>
                              )}
                              {(inv.postMode==="both"||inv.postMode==="mgmt_only") && (
                                <tr>
                                  <td style={{fontSize:9,color:"var(--mu)"}}>Mgmt Co</td>
                                  <td><span className="badge b-acc">IC Dr</span></td>
                                  <td style={{fontSize:9}}>{icDrGl?.code} {icDrGl?.name}</td>
                                  <td style={{fontSize:9,color:"var(--mu)"}}>[ICPIQ]</td>
                                  <td className="dr" style={{textAlign:"right",fontWeight:600}}>{fmt(line.amount)}</td>
                                  <td></td><td></td>
                                </tr>
                              )}
                            </Fragment>
                          ))}
                          <tr className="tbl-foot">
                            <td colSpan={4}>Totals</td>
                            <td className="dr" style={{textAlign:"right"}}>{fmt(previewDebitTotal)}</td>
                            <td className="cr" style={{textAlign:"right"}}>{fmt(previewCreditTotal)}</td>
                            <td>100%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="card-foot">
                      <button className="btn btn-ghost" onClick={()=>{setInv(blank);setManualPcts({});setPropSearch("");setActiveQueueItem(null)}}>Clear</button>
                      <button className="btn btn-acc" onClick={handlePost}>Post Journal Entry →</button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── JOURNAL LOG ── */}
            {rightPane==="journal" && !activeQueueItem && (
              <>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <span style={{fontFamily:"var(--head)",fontSize:15,fontWeight:700,color:"var(--txb)"}}>Journal Log</span>
                  <button className="btn btn-ghost btn-sm" onClick={exportMonthEntrata} title="Download this month's posted journals in Entrata's Import Journal Entries format">↓ Entrata JE (month)</button>
                </div>
                <div className="stats">
                  <div className="stat"><div className="stat-val">{journals.length}</div><div className="stat-lbl">Total JEs</div></div>
                  <div className="stat"><div className="stat-val">{fmt(totalPosted)}</div><div className="stat-lbl">Posted</div></div>
                  <div className="stat"><div className="stat-val">{journals.filter(j=>j.status==="reversed").length}</div><div className="stat-lbl">Reversed</div></div>
                  <div className="stat"><div className="stat-val">{[...new Set(journals.flatMap(j=>j.lines.map(l=>l.property.id)))].length}</div><div className="stat-lbl">Properties</div></div>
                </div>
                {journals.length===0 ? (
                  <div className="empty"><div className="empty-ico">≡</div><div style={{fontSize:10}}>No journal entries yet. Process an item from the queue to get started.</div></div>
                ) : journals.map(je=>(
                  <div key={je.id} className="je-item">
                    <div className="je-hd" onClick={()=>setExpandedJE(expandedJE===je.id?null:je.id)}>
                      <span style={{color:"var(--acc)",fontWeight:600,fontSize:10,minWidth:100,fontFamily:"var(--mono)"}}>IC-{String(je.id).slice(0,6).toUpperCase()}</span>
                      <span style={{flex:1,fontWeight:600,fontSize:11,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{je.description||je.vendor}</span>
                      <span className="badge b-acc" style={{fontSize:7}}>{je.tag}</span>
                      {je.sourceTxnId && <span className="badge b-gold" style={{fontSize:7}}>GL Pull</span>}
                      <span className="badge b-dim">{je.postMode==="both"?"Full":je.postMode==="property_only"?"Prop":"Mgmt"}</span>
                      <span style={{fontWeight:700,fontSize:12,fontVariantNumeric:"tabular-nums"}}>{fmt(je.amount)}</span>
                      <span className={`badge ${je.status==="posted"?"b-green":"b-dim"}`}>{je.status}</span>
                      <span style={{fontSize:9,color:"var(--mu)"}}>{je.lines.length}p</span>
                      {je.status==="posted" && <button className="btn btn-xs btn-ghost" onClick={e=>{e.stopPropagation();exportJournalEntrata(je)}} title="Download in Entrata's import format">↓ Entrata</button>}
                      {je.status==="posted" && <button className="btn btn-xs btn-danger" onClick={e=>{e.stopPropagation();handleReverse(je.id)}}>Reverse</button>}
                    </div>
                    {expandedJE===je.id && (
                      <div className="je-expanded scroll-x">
                        <table className="tbl">
                          <thead><tr><th>Entity</th><th>Type</th><th>GL</th><th>Memo</th><th style={{textAlign:"right"}}>Debit</th><th style={{textAlign:"right"}}>Credit</th><th>%</th></tr></thead>
                          <tbody>
                            {je.lines.map((line,i)=>(
                              <Fragment key={i}>
                                {(je.postMode==="both"||je.postMode==="property_only") && (<>
                                  <tr>
                                    <td style={{fontWeight:600,fontSize:10}}>{line.property.name}</td>
                                    <td><span className="badge b-acc">Expense</span></td>
                                    <td style={{fontSize:9}}>{je.expenseGl?.code} {je.expenseGl?.name}</td>
                                    <td style={{fontSize:9,color:"var(--mu)"}}>{je.memo} [ICPIQ]</td>
                                    <td className="dr" style={{textAlign:"right"}}>{fmt(line.amount)}</td><td></td>
                                    <td style={{fontSize:9,color:"var(--mu)"}}>{line.pct.toFixed(1)}%</td>
                                  </tr>
                                  <tr>
                                    <td className="indent">↳ {line.property.id}</td>
                                    <td><span className="badge b-gold">IC Cr</span></td>
                                    <td style={{fontSize:9}}>{je.icCreditGl?.code} {je.icCreditGl?.name}</td>
                                    <td style={{fontSize:9,color:"var(--mu)"}}>[ICPIQ]</td>
                                    <td></td><td className="cr" style={{textAlign:"right"}}>{fmt(line.amount)}</td><td></td>
                                  </tr>
                                </>)}
                                {(je.postMode==="both"||je.postMode==="mgmt_only") && (
                                  <tr>
                                    <td style={{fontSize:9,color:"var(--mu)"}}>Mgmt Co</td>
                                    <td><span className="badge b-acc">IC Dr</span></td>
                                    <td style={{fontSize:9}}>{je.icDebitGl?.code} {je.icDebitGl?.name}</td>
                                    <td style={{fontSize:9,color:"var(--mu)"}}>[ICPIQ]</td>
                                    <td className="dr" style={{textAlign:"right"}}>{fmt(line.amount)}</td><td></td><td></td>
                                  </tr>
                                )}
                              </Fragment>
                            ))}
                            <tr className="tbl-foot">
                              <td colSpan={4}>Totals</td>
                              <td className="dr" style={{textAlign:"right"}}>{fmt(je.amount*(je.postMode==="both"?2:1))}</td>
                              <td className="cr" style={{textAlign:"right"}}>{fmt(je.amount*(je.postMode==="both"?2:1))}</td>
                              <td>100%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* ── TEMPLATES ── */}
            {rightPane==="templates" && !activeQueueItem && (
              <>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <span style={{fontFamily:"var(--head)",fontSize:15,fontWeight:700,color:"var(--txb)"}}>Allocation Templates</span>
                </div>
                {templates.length===0 ? (
                  <div className="empty"><div className="empty-ico">⊟</div>
                    <div style={{fontSize:10}}>No templates yet.</div>
                    <div style={{fontSize:9,marginTop:4,color:"var(--mu)"}}>Create an allocation and click "Save as Template" to reuse it.</div>
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {templates.map(t=>{
                      const m = methods.find(x=>x.id===t.methodId);
                      return (
                        <div key={t.id} className="card" style={{marginBottom:0}}>
                          <div className="card-body" style={{display:"flex",alignItems:"center",gap:12}}>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:600,fontSize:12,marginBottom:2}}>{t.name}</div>
                              <div style={{fontSize:9,color:"var(--mu)"}}>{t.vendor} · {m?.name} · {t.selectedProps.length} properties · GL {t.expenseGl}</div>
                            </div>
                            <button className="btn btn-acc btn-sm" onClick={()=>loadTemplate(t)}>Use Template →</button>
                            <button className="btn btn-danger btn-xs" onClick={async ()=>{await supabase.from("ic_templates").delete().eq("id", t.id); loadAll();}}>×</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

          </div>
        </main>

        {/* ════════ ADMIN DRAWER ════════ */}
        <div className={`drawer-overlay ${drawerOpen?"open":""}`} onClick={()=>setDrawerOpen(false)}/>
        <div className={`drawer ${drawerOpen?"open":""}`}>
          <div className="drawer-hd">
            <span className="drawer-title">Administration</span>
            <button className="close-btn" onClick={()=>setDrawerOpen(false)}>×</button>
          </div>
          <div className="drawer-tabs">
            {[
              {id:"connectors",label:"Connectors"},
              {id:"groups",label:"Entity Groups"},
              {id:"methods",label:"Alloc Methods"},
              {id:"watchlist",label:"GL Watchlist"},
              {id:"settings",label:"Settings"},
            ].map(t=>(
              <button key={t.id} className={`dtab ${drawerTab===t.id?"on":""}`} onClick={()=>{setDrawerTab(t.id);setSelConnector(null)}}>{t.label}</button>
            ))}
          </div>
          <div className="drawer-body">

            {/* ── Connectors ── */}
            {drawerTab==="connectors" && (
              <>
                <div style={{display:"flex",gap:4,marginBottom:12,flexWrap:"wrap"}}>
                  {CONN_CATS.map(c=>(
                    <button key={c} className={`btn btn-xs ${connCat===c?"btn-acc":"btn-ghost"}`} onClick={()=>setConnCat(c)}>{c}</button>
                  ))}
                </div>
                <div className="conn-grid">
                  {filteredConns.map(c=>{
                    const isConn = connectedSystems.has(c.id);
                    const sc = STATUS_COLOR[c.status]; const sl = STATUS_LABEL[c.status];
                    return (
                      <div key={c.id} className={`conn-card ${selConnector?.id===c.id?"sel":""} ${c.status==="roadmap"?"disabled":""}`}
                        onClick={()=>{if(c.status!=="roadmap"){setSelConnector(c);setConnTab("config");setTestState(null)}}}>
                        <div className="conn-status" style={{borderColor:sc,color:sc,background:sc.replace(")",",0.08)").replace("var(","rgba(")}}>{isConn?"● Connected":sl}</div>
                        <div className="conn-top">
                          <div className="conn-logo" style={{background:c.logoColor+"15",borderColor:c.logoColor+"40",color:c.logoColor}}>{c.logo}</div>
                          <div className="conn-info">
                            <div className="conn-name">{c.name}</div>
                            <div className="conn-cat">{c.category} · {c.authType}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selConnector && (
                  <div className="cpanel">
                    <div className="cpanel-hd">
                      <div className="conn-logo" style={{width:30,height:30,fontSize:10,background:selConnector.logoColor+"15",borderColor:selConnector.logoColor+"40",color:selConnector.logoColor,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--head)",fontWeight:900,border:"1px solid"}}>{selConnector.logo}</div>
                      <div style={{flex:1}}>
                        <div style={{fontFamily:"var(--head)",fontWeight:700,fontSize:13}}>{selConnector.name}</div>
                        <div style={{fontSize:9,color:"var(--mu)"}}>{selConnector.authType}</div>
                      </div>
                      {connectedSystems.has(selConnector.id) && <span className="badge b-green">Connected</span>}
                      <button className="close-btn" onClick={()=>setSelConnector(null)}>×</button>
                    </div>
                    <div className="cpanel-tabs">
                      {["config","mapping","endpoints","howto"].map(t=>(
                        <button key={t} className={`cptab ${connTab===t?"on":""}`} onClick={()=>setConnTab(t)}>
                          {t==="config"?"Config":t==="mapping"?"GL Map":t==="endpoints"?"Endpoints":"Setup"}
                        </button>
                      ))}
                    </div>
                    <div className="cpanel-body">
                      {connTab==="config" && (<>
                        <div className="info-box">Credentials for GL pull and JE write-back to {selConnector.name}. Stored AES-256 encrypted.</div>
                        <div className="g2">
                          {["Base URL","API Key / Client ID","Client Secret","Token / Password"].map((label,i)=>(
                            <div key={i} className="field"><label className="lbl">{label}</label>
                              <input className="inp" type={i>=2?"password":"text"} placeholder="Enter value"
                                value={connCreds[`${selConnector.id}_${i}`]||""}
                                onChange={e=>setConnCreds(p=>({...p,[`${selConnector.id}_${i}`]:e.target.value}))}/>
                            </div>
                          ))}
                        </div>
                        <hr/>
                        <div className="g3">
                          <div className="field"><label className="lbl">GL Pull Freq</label><select className="sel"><option>Manual</option><option>Daily</option><option>Weekly</option></select></div>
                          <div className="field"><label className="lbl">JE Post Mode</label><select className="sel"><option>Live</option><option>Batch</option><option>Staging</option></select></div>
                          <div className="field"><label className="lbl">Memo Tag</label><input className="inp" defaultValue="[ICPIQ]"/></div>
                        </div>
                        <div style={{display:"flex",gap:6,marginTop:4}}>
                          <button className="btn btn-sm" onClick={()=>{setTestState("pending");setTimeout(()=>setTestState(Math.random()>.25?"ok":"fail"),1500)}}>{testState==="pending"?"Testing…":"Test Connection"}</button>
                          <button className="btn btn-acc btn-sm" onClick={async ()=>{await setConnectorStatus(selConnector.id, selConnector.name, "connected");setTestState("ok");msg(`${selConnector.name} connected`)}}>{connectedSystems.has(selConnector.id)?"Update":"Connect →"}</button>
                          {connectedSystems.has(selConnector.id) && <button className="btn btn-danger btn-sm" onClick={async ()=>{await setConnectorStatus(selConnector.id, selConnector.name, "disconnected");msg(`Disconnected ${selConnector.name}`)}}>Disconnect</button>}
                        </div>
                        {testState==="pending" && <div className="test-res" style={{borderColor:"var(--gold)",color:"var(--gold)",background:"var(--goldBg)"}}>Authenticating…</div>}
                        {testState==="ok"      && <div className="test-res" style={{borderColor:"var(--green)",color:"var(--green)",background:"var(--greenBg)"}}>✓ Connected — GL read/write confirmed</div>}
                        {testState==="fail"    && <div className="test-res" style={{borderColor:"var(--red)",color:"var(--red)",background:"var(--redBg)"}}>✗ Failed — 401 Unauthorized</div>}
                      </>)}
                      {connTab==="mapping" && (<>
                        <div className="info-box">Map IntercoIQ GL codes to {selConnector.name} chart of accounts.</div>
                        {["Expense GL → Debit","IC Credit (Due To) → Credit","IC Debit (Due From) → Debit","AP Clearing → Full JE"].map((m,i)=>(
                          <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 24px 1fr",gap:8,alignItems:"center",padding:"6px 0",borderBottom:"1px solid var(--b1)"}}>
                            <span style={{fontSize:10}}>{m.split("→")[0]}</span>
                            <span style={{textAlign:"center",color:"var(--mu)",fontSize:10}}>→</span>
                            <input className="inp inp-sm" placeholder={`${selConnector.name} GL`}/>
                          </div>
                        ))}
                      </>)}
                      {connTab==="endpoints" && (<>
                        <div className="g2">
                          <div><div className="lbl" style={{marginBottom:4}}>JE Write</div><div className="code-block" style={{color:"var(--acc)",fontSize:9}}>{selConnector.jeEp}</div></div>
                          <div><div className="lbl" style={{marginBottom:4}}>GL Pull</div><div className="code-block" style={{color:"var(--green)",fontSize:9}}>{selConnector.glEp}</div></div>
                        </div>
                        <div style={{marginTop:10}}><div className="lbl" style={{marginBottom:4}}>Sample Payload</div>
                          <div className="code-block">{`{
  "entity": "PROP001",
  "date": "2026-02-28",
  "memo": "IC Allocation [ICPIQ]",
  "lines": [
    { "account": "6200", "debit": 960.00 },
    { "account": "2100", "credit": 960.00 }
  ]
}`}</div></div>
                      </>)}
                      {connTab==="howto" && (
                        <div style={{display:"flex",flexDirection:"column",gap:10}}>
                          {[
                            ["Create API User",`In ${selConnector.name}, create a service account with GL read + JE write permissions.`],
                            ["Generate Credentials",`Generate ${selConnector.authType} credentials. Copy before closing.`],
                            ["Enter & Test","Paste into Config tab, click Test Connection."],
                            ["Map GL Accounts","Use GL Map tab to map IntercoIQ codes to your chart of accounts."],
                            ["Set Up Watchlist","Add GL codes to monitor in the GL Watchlist tab."],
                          ].map(([t,d],i)=>(
                            <div key={i} className="step">
                              <div className="step-n">{i+1}</div>
                              <div><div className="step-t">{t}</div><div className="step-d">{d}</div></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Property Groups ── */}
            {drawerTab==="groups" && (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{fontSize:10,color:"var(--mu)"}}>{groups.length} groups · {properties.length} entities</span>
                  <button className="btn btn-sm btn-ghost" onClick={()=>setShowGroupForm(v=>!v)}>{showGroupForm?"Cancel":"+ New Group"}</button>
                </div>
                {showGroupForm && (
                  <div className="card" style={{marginBottom:14,background:"var(--accBg)"}}>
                    <div className="card-body">
                      <div className="field"><label className="lbl">Group Name *</label><input className="inp" value={newGroup.name} onChange={e=>setNewGroup(p=>({...p,name:e.target.value}))} placeholder="e.g. Southwest Managed"/></div>
                      <div className="g3">
                        {GROUP_ATTRS.map(attr=>{
                          const vals=[...new Set(properties.map(p=>p[attr]))];
                          return (
                            <div key={attr} className="field"><label className="lbl">{attr}</label>
                              <select className="sel" value={newGroup.filters[attr]||""} onChange={e=>setNewGroup(p=>({...p,filters:{...p.filters,[attr]:e.target.value||undefined}}))}>
                                <option value="">Any</option>{vals.map(v=><option key={v} value={v}>{v}</option>)}
                              </select></div>
                          );
                        })}
                      </div>
                      {Object.values(newGroup.filters).some(Boolean) && (
                        <div style={{fontSize:9,color:"var(--mu)",marginBottom:8}}>Matches: {properties.filter(p=>Object.entries(newGroup.filters).filter(([,v])=>v).every(([k,v])=>p[k]===v)).map(p=>p.name).join(", ")||"None"}</div>
                      )}
                      <button className="btn btn-acc btn-sm" onClick={async ()=>{
                        if (!newGroup.name) return;
                        const { error } = await supabase.from("entity_groups").insert({ org_id: org.id, name: newGroup.name, filters: newGroup.filters });
                        if (error) { msg(`Could not save group: ${error.message}`); return; }
                        setNewGroup({name:"",filters:{}});setShowGroupForm(false);msg("Group created");
                        loadAll();
                      }}>Create Group</button>
                    </div>
                  </div>
                )}
                <div className="grp-grid">
                  {groups.map(g=>{
                    const matched=properties.filter(p=>Object.entries(g.filters).every(([k,v])=>p[k]===v));
                    return (
                      <div key={g.id} className="card" style={{marginBottom:0}}>
                        <div className="card-hd"><span className="card-title">{g.name}</span><span style={{fontSize:9,color:"var(--mu)"}}>{matched.length}p</span></div>
                        <div className="card-body" style={{padding:"8px 12px"}}>
                          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:6}}>
                            {Object.entries(g.filters).filter(([,v])=>v).map(([k,v])=>(
                              <span key={k} style={{fontSize:8,padding:"1px 5px",border:"1px solid var(--b2)",color:"var(--mu)"}}>{k}: {v}</span>
                            ))}
                          </div>
                          <div style={{fontSize:9,color:"var(--mu)",lineHeight:1.4}}>{matched.map(p=>p.name).join(", ")}</div>
                        </div>
                        <div className="card-foot"><button className="btn btn-xs btn-danger" onClick={async ()=>{await supabase.from("entity_groups").delete().eq("id", g.id); loadAll();}}>Delete</button></div>
                      </div>
                    );
                  })}
                </div>
                <hr style={{margin:"16px 0"}}/>
                <div style={{fontSize:9,letterSpacing:".12em",textTransform:"uppercase",color:"var(--mu)",marginBottom:8}}>Entity Master</div>
                <table className="tbl"><thead><tr><th>ID</th><th>Name</th><th style={{textAlign:"right"}}>Units</th><th style={{textAlign:"right"}}>Emp</th><th>Region</th><th>Division</th><th>Own</th></tr></thead>
                  <tbody>{properties.map(p=>(
                    <tr key={p.id}>
                      <td style={{color:"var(--acc)",fontWeight:600,fontSize:9,fontFamily:"var(--mono)"}}>{String(p.id).slice(0,8)}</td>
                      <td style={{fontWeight:500,fontSize:10}}>{p.name}</td>
                      <td style={{textAlign:"right"}}>{p.units}</td>
                      <td style={{textAlign:"right"}}>{p.employees}</td>
                      <td style={{fontSize:9,color:"var(--mu)"}}>{p.region}</td>
                      <td style={{fontSize:9,color:"var(--mu)"}}>{p.division}</td>
                      <td style={{fontSize:9,color:"var(--mu)"}}>{p.ownership}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </>
            )}

            {/* ── Allocation Methods ── */}
            {drawerTab==="methods" && (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{fontSize:10,color:"var(--mu)"}}>{methods.length} methods</span>
                  <button className="btn btn-sm btn-ghost" onClick={()=>setShowMethodForm(v=>!v)}>{showMethodForm?"Cancel":"+ New Method"}</button>
                </div>
                {showMethodForm && (
                  <div className="card" style={{marginBottom:14,background:"var(--accBg)"}}>
                    <div className="card-body">
                      <div className="g2">
                        <div className="field"><label className="lbl">Name *</label><input className="inp" value={newMethod.name} onChange={e=>setNewMethod(p=>({...p,name:e.target.value}))} placeholder="e.g. By Sq Footage"/></div>
                        <div className="field"><label className="lbl">Driver *</label>
                          <select className="sel" value={newMethod.driver} onChange={e=>setNewMethod(p=>({...p,driver:e.target.value}))}>
                            <option value="equal">Equal Split</option><option value="units">By Units</option><option value="employees">By Headcount</option><option value="manual">Manual %</option>
                          </select></div>
                      </div>
                      <div className="field"><label className="lbl">Description</label><input className="inp" value={newMethod.description} onChange={e=>setNewMethod(p=>({...p,description:e.target.value}))}/></div>
                      <button className="btn btn-acc btn-sm" onClick={async ()=>{
                        if (!newMethod.name) return;
                        const { error } = await supabase.from("alloc_methods").insert({ org_id: org.id, name: newMethod.name, description: newMethod.description || null, driver: newMethod.driver, is_custom: true });
                        if (error) { msg(`Could not save method: ${error.message}`); return; }
                        setNewMethod({name:"",description:"",driver:"equal"});setShowMethodForm(false);msg("Method saved");
                        loadAll();
                      }}>Save Method</button>
                    </div>
                  </div>
                )}
                {methods.map(m=>(
                  <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid var(--b1)"}}>
                    <div style={{flex:1}}><div style={{fontWeight:600,fontSize:11}}>{m.name}</div><div style={{fontSize:9,color:"var(--mu)",marginTop:1}}>{m.description}</div></div>
                    <span style={{fontSize:8,padding:"2px 5px",border:"1px solid var(--di)",color:"var(--mu)"}}>{m.driver}</span>
                    <span className={`badge ${m.custom?"b-acc":"b-green"}`}>{m.custom?"Custom":"System"}</span>
                  </div>
                ))}
              </>
            )}

            {/* ── GL Watchlist ── */}
            {drawerTab==="watchlist" && (
              <>
                <div className="info-box">Watched GL accounts surface unallocated transactions in your allocation queue.</div>
                <div style={{display:"flex",gap:6,marginBottom:12}}>
                  <select className="sel" style={{flex:1}} value="" onChange={e=>{if(e.target.value){saveWatchedGLs([...watchedGLs, e.target.value])}}}>
                    <option value="">+ Add GL to watchlist…</option>
                    {glAccounts.filter(g=>g.entity==="expense"&&!watchedGLs.includes(g.code)).map(g=>(
                      <option key={g.code} value={g.code}>{g.code} – {g.name}</option>
                    ))}
                  </select>
                </div>
                {watchedGLs.map(code=>{
                  const g=glAccounts.find(x=>x.code===code);
                  const count = glTxns.filter(t=>t.gl===code&&!t.allocated).length;
                  return (
                    <div key={code} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:"1px solid var(--b1)"}}>
                      <span style={{fontSize:11,color:"var(--acc)",fontWeight:600,minWidth:40}}>{code}</span>
                      <span style={{fontSize:11,flex:1}}>{g?.name}</span>
                      {count>0 && <span className="badge b-gold">{count} pending</span>}
                      <button className="btn btn-ghost btn-xs" onClick={()=>saveWatchedGLs(watchedGLs.filter(x=>x!==code))}>Remove</button>
                    </div>
                  );
                })}
              </>
            )}

            {/* ── Settings ── */}
            {drawerTab==="settings" && (
              <>
                {[
                  {label:"Shared Entity Master with AmortIQ", desc:"Entities, groups, and attributes sync across PIQ modules", status:"active"},
                  {label:"[ICPIQ] Memo Tagging",                  desc:"Auto-tag all JEs for GL recon traceability", status:"active"},
                  {label:"Append-Only JE Ledger",                 desc:"JEs cannot be deleted — only reversed", status:"active"},
                  {label:"Allocation Templates",                   desc:"Save and reuse allocation configurations", status:"active"},
                  {label:"Approval Workflow",                      desc:"Require controller sign-off before posting", status:"soon"},
                  {label:"Period Locking",                         desc:"Prevent allocations to closed periods", status:"soon"},
                  {label:"Invoice OCR / AP Inbox",                 desc:"Email-in invoices; OCR extracts vendor, amount, GL", status:"roadmap"},
                  {label:"CAO Rollup Dashboard",                   desc:"IC allocation summary by group, division, or region", status:"roadmap"},
                  {label:"Elimination Journal Auto-Generation",    desc:"Auto-generate IC elimination entries for consolidated reporting", status:"roadmap"},
                ].map((s,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid var(--b1)"}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:11,fontWeight:600}}>{s.label}</div>
                      <div style={{fontSize:9,color:"var(--mu)",marginTop:1}}>{s.desc}</div>
                    </div>
                    <span className={`badge ${s.status==="active"?"b-green":s.status==="soon"?"b-gold":"b-dim"}`}>
                      {s.status==="active"?"Active":s.status==="soon"?"Soon":"Roadmap"}
                    </span>
                  </div>
                ))}
              </>
            )}

          </div>
        </div>

        {/* ════════ GL IMPORT MODAL ════════ */}
        {showImportModal && (
          <div className="modal-overlay" onClick={()=>{setShowImportModal(false);setImportPreview([]);setImportError("")}}>
            <div className="modal" style={{maxWidth:560,width:"90%"}} onClick={e=>e.stopPropagation()}>
              <div className="modal-title">Import GL Balances</div>
              <div className="modal-text" style={{marginBottom:14,lineHeight:1.7}}>
                Upload a CSV with your ERP's IC account balances. IntercoIQ will compare these against its own posted amounts to surface any discrepancies before you run consolidation eliminations.
              </div>
              <div style={{fontSize:9,color:"var(--mu)",marginBottom:10,lineHeight:1.6}}>
                <strong style={{color:"var(--tx)"}}>Required columns:</strong> property_id, due_to_balance, due_from_balance<br/>
                <strong style={{color:"var(--tx)"}}>Supported ERPs:</strong> Entrata, Yardi, MRI, RealPage — export your IC GL accounts (e.g. 2100, 1100) to CSV
              </div>

              {/* Template download */}
              <button className="btn btn-ghost btn-sm" style={{marginBottom:14}} onClick={()=>{
                const header = "property_id,property_name,due_to_balance,due_from_balance";
                const props_ = [
                  {id:"P001",name:"Elmwood Apartments"},{id:"P002",name:"Riverside Lofts"},
                  {id:"P003",name:"The Carrington"},{id:"P004",name:"Millbrook Commons"},
                  {id:"P005",name:"Sunstone Villas"},{id:"P006",name:"Harbor View Tower"},
                  {id:"P007",name:"Pine Ridge Estates"},{id:"P008",name:"Westgate Flats"},
                ];
                const rows = props_.map(p=>`${p.id},"${p.name}",0.00,0.00`);
                const csv = [header,...rows].join("\n");
                const blob = new Blob([csv],{type:"text/csv"});
                const url = URL.createObjectURL(blob); const a=document.createElement("a");
                a.href=url; a.download="IC_GL_Import_Template.csv"; a.click();
              }}>⬇ Download Template CSV</button>

              {/* File drop zone */}
              <div className="import-drop"
                onDragOver={e=>e.preventDefault()}
                onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){
                  const r=new FileReader();r.onload=ev=>{
                    const t=ev.target.result;
                    const lines=t.trim().split(/\r?\n/);
                    if(lines.length<2){setImportError("File appears empty");setImportPreview([]);return;}
                    const hdr=lines[0].split(",").map(h=>h.trim().toLowerCase());
                    const idIdx=hdr.findIndex(h=>h.includes("property")||h==="id"||h==="entity");
                    const dtIdx=hdr.findIndex(h=>h.includes("due_to")||h.includes("dueto")||h.includes("due to"));
                    const dfIdx=hdr.findIndex(h=>h.includes("due_from")||h.includes("duefrom")||h.includes("due from"));
                    if(idIdx<0||dtIdx<0||dfIdx<0){setImportError("Expected columns: property_id, due_to_balance, due_from_balance");setImportPreview([]);return;}
                    const rows=[];
                    for(let i=1;i<lines.length;i++){const cols=lines[i].split(",");if(cols.length<3)continue;const propId=cols[idIdx]?.trim();const dueTo=parseFloat(cols[dtIdx]?.replace(/[$,]/g,""))||0;const dueFrom=parseFloat(cols[dfIdx]?.replace(/[$,]/g,""))||0;if(propId)rows.push({propId,dueTo,dueFrom});}
                    setImportError(""); setImportPreview(rows);
                  };r.readAsText(f);}}}
                onClick={()=>document.getElementById("gl-import-input").click()}>
                <input id="gl-import-input" type="file" accept=".csv" style={{display:"none"}}
                  onChange={e=>{const f=e.target.files[0];if(!f)return;
                    const r=new FileReader();r.onload=ev=>{
                      const t=ev.target.result;const lines=t.trim().split(/\r?\n/);
                      if(lines.length<2){setImportError("File appears empty");setImportPreview([]);return;}
                      const hdr=lines[0].split(",").map(h=>h.trim().toLowerCase());
                      const idIdx=hdr.findIndex(h=>h.includes("property")||h==="id"||h==="entity");
                      const dtIdx=hdr.findIndex(h=>h.includes("due_to")||h.includes("dueto")||h.includes("due to"));
                      const dfIdx=hdr.findIndex(h=>h.includes("due_from")||h.includes("duefrom")||h.includes("due from"));
                      if(idIdx<0||dtIdx<0||dfIdx<0){setImportError("Expected columns: property_id, due_to_balance, due_from_balance");setImportPreview([]);return;}
                      const rows=[];
                      for(let i=1;i<lines.length;i++){const cols=lines[i].split(",");if(cols.length<3)continue;const propId=cols[idIdx]?.trim();const dueTo=parseFloat(cols[dtIdx]?.replace(/[$,]/g,""))||0;const dueFrom=parseFloat(cols[dfIdx]?.replace(/[$,]/g,""))||0;if(propId)rows.push({propId,dueTo,dueFrom});}
                      setImportError(""); setImportPreview(rows);
                    };r.readAsText(f);}}/>
                <div style={{fontSize:10,color:"var(--mu)",marginBottom:4}}>Drop CSV here or click to browse</div>
                <div style={{fontSize:9,color:"var(--di)"}}>property_id · due_to_balance · due_from_balance</div>
              </div>

              {importError && (
                <div style={{background:"var(--redBg)",border:"1px solid rgba(212,84,84,.3)",padding:"8px 12px",fontSize:9,color:"var(--red)",marginBottom:10}}>{importError}</div>
              )}

              {importPreview.length > 0 && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"var(--acc)",fontWeight:600,marginBottom:8}}>{importPreview.length} rows parsed — preview</div>
                  <table className="elim-tbl" style={{fontSize:10}}>
                    <thead>
                      <tr><th>Entity ID</th><th className="r">Due-To</th><th className="r">Due-From</th><th className="r">Net</th></tr>
                    </thead>
                    <tbody>
                      {importPreview.slice(0,6).map((r,i)=>(
                        <tr key={i}>
                          <td>{r.propId}</td>
                          <td className="r" style={{color:"var(--green)"}}>{fmt(r.dueTo)}</td>
                          <td className="r" style={{color:"var(--acc)"}}>{fmt(r.dueFrom)}</td>
                          <td className="r" style={{color:Math.abs(r.dueTo-r.dueFrom)<0.01?"var(--green)":"var(--red)",fontWeight:600}}>
                            {Math.abs(r.dueTo-r.dueFrom)<0.01?"✓":fmt(r.dueTo-r.dueFrom)}
                          </td>
                        </tr>
                      ))}
                      {importPreview.length>6 && <tr><td colSpan={4} style={{color:"var(--mu)",fontSize:9}}>…and {importPreview.length-6} more rows</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="modal-actions">
                <button className="btn btn-ghost btn-sm" onClick={()=>{setShowImportModal(false);setImportPreview([]);setImportError("")}}>Cancel</button>
                <button className="btn btn-acc btn-sm"
                  disabled={importPreview.length===0}
                  style={{opacity:importPreview.length===0?0.4:1}}
                  onClick={async ()=>{
                    const rows = importPreview.map((r) => ({ org_id: org.id, period: CUR_PERIOD, entity_id: r.propId, due_to: r.dueTo, due_from: r.dueFrom }));
                    const { error } = await supabase.from("ic_gl_balances").upsert(rows, { onConflict: "org_id,period,entity_id" });
                    if (error) { setImportError(`Import failed: ${error.message} — the entity ID column must contain the IDs from the downloaded template.`); return; }
                    setGlImportRows(importPreview);
                    setShowImportModal(false);setImportPreview([]);setImportError("");
                    setConsolidationTab("elim");
                    msg(`GL balances imported — ${importPreview.length} entities`);
                  }}>Confirm Import →</button>
              </div>
            </div>
          </div>
        )}

        {/* ════════ CONFIRM MODAL ════════ */}
        {confirmModal && (
          <div className="modal-overlay" onClick={()=>setConfirmModal(null)}>
            <div className="modal" onClick={e=>e.stopPropagation()}>
              <div className="modal-title">{confirmModal.title}</div>
              <div className="modal-text">{confirmModal.text}</div>
              <div className="modal-actions">
                <button className="btn btn-ghost btn-sm" onClick={()=>setConfirmModal(null)}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={confirmModal.onConfirm}>Confirm Reversal</button>
              </div>
            </div>
          </div>
        )}

        {flash && <div className="flash">{flash}</div>}
      </div>
    </>
  );
}
